// @ts-nocheck
/* * INSTRUCCIONES PARA CODESANDBOX:
 * 1. Crea un proyecto en CodeSandbox usando la plantilla de React (o Vite + React).
 * 2. En la pestaña de dependencias, añade: `lucide-react` y `firebase`.
 * 3. Listo para usar.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Printer,
  Share2,
  Mail,
  Plus,
  Minus,
  Trash2,
  FileText,
  Loader2,
  Settings,
  History,
  Save,
  Image as ImageIcon,
  CheckCircle,
  Copy,
  AlertTriangle,
  Search,
  PieChart,
  Upload,
  Eye,
  Edit,
  Copy as CopyIcon,
  FileSpreadsheet,
  Calculator,
  RefreshCw,
  Globe,
  ChevronDown,
  ChevronUp,
  Users,
  X,
  ArrowRightLeft,
  Lock,
  LogOut,
  Sparkles,
  Landmark,
  ExternalLink,
  Briefcase,
  CalendarClock,
  Zap,
  Link,
  ShieldCheck,
  Radar,
  Clock,
} from "lucide-react";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  signInAnonymously,
  signInWithCustomToken,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc as updateFirestoreDoc,
} from "firebase/firestore";

// --- FUNCIÓN ESCUDO CONTRA EL AUTO-FORMATEO DE ENLACES ---
const buildSecureUrl = (domain, path) => {
  return ["h", "t", "t", "p", "s", ":", "/", "/", domain, path].join("");
};

// --- CONFIGURACIÓN DE FIREBASE ---
const firebaseConfig =
  typeof window !== "undefined" &&
  typeof window.__firebase_config !== "undefined"
    ? JSON.parse(window.__firebase_config)
    : typeof __firebase_config !== "undefined"
    ? JSON.parse(__firebase_config)
    : {
        apiKey: "AIzaSyA6TzfZLFpIz-cBdi5qtsKBzyC-yb-2U8M",
        authDomain: "keops-platform-7d98a.firebaseapp.com",
        projectId: "keops-platform-7d98a",
        storageBucket: "keops-platform-7d98a.firebasestorage.app",
        messagingSenderId: "456715350427",
        appId: "1:456715350427:web:bd75ee074102902451b13e",
      };

const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
const auth = getAuth(app);
const db = getFirestore(app);

const getAppId = () => {
  if (
    typeof window !== "undefined" &&
    typeof window.__app_id !== "undefined" &&
    window.__app_id !== null
  )
    return String(window.__app_id);
  if (typeof __app_id !== "undefined" && __app_id !== null)
    return String(__app_id);
  return "keops-platform";
};
const safeAppId = getAppId();

const publicDataPath = (collectionName) =>
  collection(db, "artifacts", safeAppId, "public", "data", collectionName);
const publicDocPath = (collectionName, documentId) =>
  doc(
    db,
    "artifacts",
    safeAppId,
    "public",
    "data",
    collectionName,
    String(documentId).trim()
  );

// ==========================================
// 🎨 PERSONALIZACIÓN DE LOGOS
const PLATFORM_LOGO_URL = "/logo.png";
const LOGIN_LOGO_URL = "/image.gif";
// ==========================================

// --- CONFIGURACIÓN DE GEMINI API ---
const getGeminiKey = () => {
  let envKey = null;
  try {
    envKey = import.meta.env.VITE_GEMINI_API_KEY;
  } catch (e) {}
  if (!envKey) {
    try {
      envKey = process.env.REACT_APP_GEMINI_API_KEY;
    } catch (e) {}
  }
  return envKey || "AQ.Ab8RN6Ku0zIzNgRomjfO3YXZrwTq9Xtjl6RMNO5Yr-5I-6qj6g";
};

const callGeminiAPI = async (
  promptText,
  isJson = false,
  systemInstruction = null,
  imageBase64 = null,
  mimeType = null
) => {
  const geminiApiKey = getGeminiKey();

  if (!geminiApiKey || geminiApiKey.trim() === "") {
    throw new Error("La API Key está vacía en el código.");
  }

  let finalPrompt = promptText;
  if (systemInstruction) {
    finalPrompt = `Instrucciones del sistema:\n${systemInstruction}\n\n---\n\nSolicitud del usuario:\n${promptText}`;
  }
  if (isJson) {
    finalPrompt +=
      "\n\nIMPORTANTE: Devuelve estricta y únicamente un objeto JSON válido. NO uses bloques markdown (como ```json) y no incluyas texto explicativo extra.";
  }

  const parts = [{ text: finalPrompt }];

  if (imageBase64) {
    parts.push({
      inlineData: { mimeType: mimeType || "image/jpeg", data: imageBase64 },
    });
  }

  const payload = {
    contents: [{ role: "user", parts: parts }],
    generationConfig: { temperature: 0.2 },
  };

  // Usamos el modelo 1.5-flash oficial
  const currentModel = "gemini-1.5-flash";

  try {
    const geminiUrl = buildSecureUrl(
      "generativelanguage.googleapis.com",
      `/v1beta/models/${currentModel}:generateContent?key=${geminiApiKey.trim()}`
    );

    const response = await fetch(geminiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const exactError = errData.error?.message || `HTTP ${response.status}`;

      if (exactError.includes("suspended")) {
        throw new Error(
          "La API Key ha sido suspendida. Por favor, crea una nueva en un proyecto diferente y añádela a las variables de entorno de Vercel."
        );
      }
      // Lanza el error REAL de Google
      throw new Error(
        `Google IA rechazó la conexión. Razón exacta: ${exactError}`
      );
    }

    const data = await response.json();
    let text = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (!text) throw new Error("Respuesta vacía de Gemini");

    if (isJson)
      text = text
        .replace(/```json/gi, "")
        .replace(/```/g, "")
        .trim();

    return text;
  } catch (error) {
    throw new Error(error.message);
  }
};

// --- INYECCIÓN TEMPRANA DE ESTILOS Y METADATOS ---
if (typeof document !== "undefined") {
  document.title = "KEOPS";
  let favicon = document.querySelector("link[rel~='icon']");
  if (!favicon) {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    document.head.appendChild(favicon);
  }
  favicon.href = "/icon.png";

  let viewport = document.querySelector("meta[name=viewport]");
  if (!viewport) {
    viewport = document.createElement("meta");
    viewport.name = "viewport";
    document.head.appendChild(viewport);
  }
  viewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";

  if (!document.getElementById("tailwind-config")) {
    const configScript = document.createElement("script");
    configScript.id = "tailwind-config";
    configScript.innerHTML = `window.tailwind = { config: { darkMode: 'class' } }`;
    document.head.appendChild(configScript);
  }
  if (!document.getElementById("tailwind-cdn")) {
    const script = document.createElement("script");
    script.id = "tailwind-cdn";
    script.src = buildSecureUrl("cdn.tailwindcss.com", "");
    document.head.appendChild(script);
  }
  if (!document.getElementById("html2pdf-cdn")) {
    const script = document.createElement("script");
    script.id = "html2pdf-cdn";
    script.src = buildSecureUrl(
      "cdnjs.cloudflare.com",
      "/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"
    );
    document.head.appendChild(script);
  }
}

// --- UTILIDADES ---
const bancosHonduras = [
  "Banco Atlántida",
  "BAC Credomatic",
  "Banco Ficohsa",
  "Banpaís",
  "Banco Promerica",
  "Banco de Occidente",
  "Banco de los Trabajadores",
  "Banrural",
  "Banco Lafise",
  "Banco Popular",
  "Banhcafé",
  "Otro",
];

const getLocalDateString = () => {
  const d = new Date();
  const tzoffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzoffset).toISOString().split("T")[0];
};

const formatLocalDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}/${month}/${year}`;
};

const safeGetTime = (dateVal) => {
  if (!dateVal) return 0;
  const t = new Date(dateVal).getTime();
  return isNaN(t) ? 0 : t;
};

const formatMoney = (amount) => {
  return Number(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const numeroALetras = (numero) => {
  const unidades = [
    "",
    "UN ",
    "DOS ",
    "TRES ",
    "CUATRO ",
    "CINCO ",
    "SEIS ",
    "SIETE ",
    "OCHO ",
    "NUEVE ",
  ];
  const decenas = [
    "DIEZ ",
    "ONCE ",
    "DOCE ",
    "TRECE ",
    "CATORCE ",
    "QUINCE ",
    "DIECISEIS ",
    "DIECISIETE ",
    "DIECIOCHO ",
    "DIECINUEVE ",
    "VEINTE ",
    "TREINTA ",
    "CUARENTA ",
    "CINCUENTA ",
    "SESENTA ",
    "SETENTA ",
    "OCHENTA ",
    "NOVENTA ",
  ];
  const centenas = [
    "",
    "CIENTO ",
    "DOSCIENTOS ",
    "TRESCIENTOS ",
    "CUATROCIENTOS ",
    "QUINIENTOS ",
    "SEISCIENTOS ",
    "SETECIENTOS ",
    "OCHOCIENTOS ",
    "NOVECIENTOS ",
  ];

  let enteros = Math.floor(numero);
  let centavos = Math.round((numero - enteros) * 100);
  if (enteros === 0) return `CERO LEMPIRAS CON ${centavos}/100 CENTAVOS`;

  const convertirGrupo = (n) => {
    let output = "";
    if (n === 100) return "CIEN ";
    if (n > 99) {
      output += centenas[Math.floor(n / 100)];
      n %= 100;
    }
    if (n > 9 && n < 20) {
      output += decenas[n - 10];
      return output;
    }
    if (n > 19) {
      let d = Math.floor(n / 10);
      output += decenas[d + 8];
      n %= 10;
      if (n > 0) output += "Y ";
    }
    if (n > 0) output += unidades[n];
    return output;
  };

  let letras = "";
  if (enteros >= 1000000) {
    let millones = Math.floor(enteros / 1000000);
    letras +=
      millones === 1 ? "UN MILLON " : convertirGrupo(millones) + "MILLONES ";
    enteros %= 1000000;
  }
  if (enteros >= 1000) {
    let miles = Math.floor(enteros / 1000);
    letras += miles === 1 ? "MIL " : convertirGrupo(miles) + "MIL ";
    enteros %= 1000;
  }
  if (enteros > 0) letras += convertirGrupo(enteros);
  return `${letras.trim()} LEMPIRAS CON ${centavos}/100 CENTAVOS`;
};

const getNextDocNumber = (type, docs) => {
  const typeDocs = docs.filter((d) => d.type === type && d.docNumber);
  if (typeDocs.length === 0) return "000-001-01-00000001";
  let maxNum = 0;
  let prefix = "000-001-01-";
  typeDocs.forEach((d) => {
    const parts = d.docNumber.split("-");
    if (parts.length === 4) {
      const num = parseInt(parts[3], 10);
      if (!isNaN(num) && num > maxNum) {
        maxNum = num;
        prefix = `${parts[0]}-${parts[1]}-${parts[2]}-`;
      }
    } else {
      const match = d.docNumber.match(/(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (!isNaN(num) && num > maxNum) {
          maxNum = num;
          prefix = d.docNumber.substring(
            0,
            d.docNumber.length - match[1].length
          );
        }
      }
    }
  });
  if (maxNum === 0) return "000-001-01-00000001";
  return `${prefix}${String(maxNum + 1).padStart(8, "0")}`;
};

const resizeImage = (file, maxWidth, maxHeight) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/png"));
      };
    };
    reader.onerror = (error) => reject(error);
  });
};

const copyToClipboard = (text, callback) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand("copy");
    if (callback) callback();
  } catch (err) {
    console.error("Error al copiar", err);
  }
  document.body.removeChild(textArea);
};

const getTotals = (data) => {
  const subtotal = (data.items || []).reduce(
    (acc, item) => acc + item.quantity * item.unitPrice,
    0
  );
  const impExonerado = parseFloat(data.importeExonerado) || 0;
  const impExento = parseFloat(data.importeExento) || 0;
  const impGravado18 = parseFloat(data.importeGravado18) || 0;

  const applyISV = data.applyISV !== false;
  const impGravado15_calc = applyISV
    ? Math.max(0, subtotal - impExonerado - impExento - impGravado18)
    : 0;
  const impGravado15 =
    data.importeGravado15 !== undefined && data.importeGravado15 !== ""
      ? parseFloat(data.importeGravado15)
      : impGravado15_calc;

  const isv15_calc = impGravado15 * 0.15;
  const isv15 =
    data.isv15 !== undefined && data.isv15 !== ""
      ? parseFloat(data.isv15)
      : isv15_calc;

  const isv18_calc = impGravado18 * 0.18;
  const isv18 =
    data.isv18 !== undefined && data.isv18 !== ""
      ? parseFloat(data.isv18)
      : isv18_calc;

  const total =
    data.type === "RECIBO"
      ? parseFloat(data.receiptTotal) || 0
      : subtotal + (applyISV ? isv15 || 0 : 0) + (isv18 || 0);
  const balance = total - (parseFloat(data.advance) || 0);
  const totalInWords = numeroALetras(total);

  return {
    subtotal,
    impExonerado,
    impExento,
    impGravado18,
    impGravado15,
    isv15,
    isv18,
    total,
    balance,
    totalInWords,
    impGravado15_calc,
    isv15_calc,
    isv18_calc,
  };
};

const getStatusColor = (status) => {
  switch (status) {
    case "APROBADA":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "RECHAZADA":
      return "bg-red-100 text-red-800 border-red-200";
    case "PENDIENTE DE PAGO":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "PAGADO":
      return "bg-[#252a50] text-white border-transparent";
    default:
      return "bg-gray-100 text-gray-700 border-gray-300";
  }
};

// --- ESTILO MOBBIN CLARO ---
const mobbinInput =
  "w-full max-w-full box-border px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-[#252a50] focus:border-[#252a50] text-black outline-none transition-all placeholder-gray-400";
const md3Card =
  "bg-white rounded-2xl md:rounded-3xl border border-gray-200 transition-colors shadow-sm";
const mobbinBtnPrimary =
  "bg-[#252a50] text-white rounded-xl px-4 py-2.5 text-sm font-medium transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2";
const mobbinBtnTonal =
  "bg-gray-100 hover:bg-gray-200 text-[#252a50] rounded-xl px-4 py-2.5 text-sm font-bold transition-all active:scale-[0.98] flex items-center justify-center gap-2";
const mobbinBtnOutlined =
  "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 rounded-xl px-4 py-2.5 text-sm font-medium transition-all active:scale-[0.98] flex items-center justify-center gap-2";

export default function App() {
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const [zoomLevel, setZoomLevel] = useState(100);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setZoomLevel(50);
    }
  }, []);

  const [isUnlocked, setIsUnlocked] = useState(() => {
    if (typeof window !== "undefined")
      return localStorage.getItem("keopsUnlocked") === "true";
    return false;
  });
  const [passInput, setPassInput] = useState("");
  const [authError, setAuthError] = useState("");

  // INICIAMOS DIRECTAMENTE EN EL EDITOR PARA OCULTAR LICITACIONES MIENTRAS SE DESARROLLA
  const [activeTab, setActiveTab] = useState("editor");
  const [notification, setNotification] = useState("");

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== "undefined")
      return localStorage.getItem("keopsDarkMode") === "true";
    return false;
  });

  const defaultSettings = {
    format: "Letter",
    bgColor: "#ffffff",
    textColor: "#000000",
    primaryColor: "#252a50",
    fontFamily: "Arial, sans-serif",
    textScale: 1,
    headerTitle: "REPRESENTACIONES KEOPS",
    headerText:
      "Un ecosistema de soluciones diseñado para la excelencia empresarial.",
    footerText:
      "Si tiene alguna consulta, póngase en contacto con nosotros.\nTegucigalpa, Honduras.\nTel: (+504) 9911-6745",
    logoImg: null,
    signatureImg: null,
    stampImg: null,
    watermarkImg: null,
    itemsPerPage: 8,
    honducomprasKeywords:
      "banderas, uniformes, gorras, sublimacion, confeccion, bordado, serigrafia, eventos, almuerzos, cenas, desayunos, catering",
    honducomprasDays: "7",
    honducomprasWebhook:
      "[https://535fc508-2aca-45c3-8a30-07c90cb19899-00-2sa1avks8a264.janeway.replit.dev/scrape-honducompras](https://535fc508-2aca-45c3-8a30-07c90cb19899-00-2sa1avks8a264.janeway.replit.dev/scrape-honducompras)",
    emailWebhook: "",
    defaultCaiFactura: "E29670-B1BEBB-A642B8-EBDBE3-1029FA-52",
    defaultCaiProforma: "28D927-519E00-FB04E0-63BE03-0909F7-D2",
  };
  const [settings, setSettings] = useState(defaultSettings);

  const defaultDoc = {
    id: null,
    type: "COTIZACIÓN",
    date: getLocalDateString(),
    docNumber: "000-001-01-00000001",
    cai: "E29670-B1BEBB-A642B8-EBDBE3-1029FA-52",
    clientName: "",
    clientCompany: "",
    clientRTN: "",
    clientAddress: "",
    author: "",
    items: [{ id: 1, quantity: 1, description: "", unitPrice: 0 }],
    notes: "",
    applyISV: true,
    showSignature: true,
    showStamp: true,
    importeExonerado: 0,
    importeExento: 0,
    importeGravado18: 0,
    importeGravado15: "",
    isv15: "",
    isv18: "",
    exemptOrder: "",
    exemptRegistry: "",
    sagRegistry: "",
    receiptConcept: "",
    paymentMethod: "EFECTIVO",
    bank: "",
    accountNumber: "",
    checkNumber: "",
    otNumber: "",
    ceNumber: "",
    advance: 0,
    receiptTotal: "",
    receiverName: "",
    receiverDNI: "",
    receiveTime: "12:00",
    isGovProcess: false,
    govProcessCode: "",
    customItemsPerPage: "",
  };

  const [docData, setDocData] = useState(defaultDoc);
  const currentTotals = getTotals(docData);

  const [historyDocs, setHistoryDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState({
    name: "",
    rtn: "",
    phone: "",
    email: "",
    address: "",
    contact: "",
  });

  // HONDUCOMPRAS BIDS STATE
  const [bids, setBids] = useState([]);
  const [newBid, setNewBid] = useState({
    code: "",
    title: "",
    deadline: "",
    entity: "",
    contactEmail: "",
    link: "",
    status: "PENDIENTE",
  });
  const [bidSearchTerm, setBidSearchTerm] = useState("");
  const [isSyncingBids, setIsSyncingBids] = useState(false);
  const [autoApplyingId, setAutoApplyingId] = useState(null);

  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientSortOrder, setClientSortOrder] = useState("asc");

  const [historySearch, setHistorySearch] = useState("");
  const [historySortConfig, setHistorySortConfig] = useState({
    field: "number",
    order: "desc",
  });
  const [previewDoc, setPreviewDoc] = useState(null);

  const [loadingItemIds, setLoadingItemIds] = useState(new Set());
  const [isGeneratingNotes, setIsGeneratingNotes] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [isEnhancingAll, setIsEnhancingAll] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const [exchangeRates, setExchangeRates] = useState(null);
  const [calcInput, setCalcInput] = useState("");
  const [showCalculator, setShowCalculator] = useState(false);
  const [convAmount, setConvAmount] = useState("");
  const [convFrom, setConvFrom] = useState("HNL");
  const [convTo, setConvTo] = useState("USD");
  const [convResult, setConvResult] = useState(0);

  const [showImportBox, setShowImportBox] = useState(false);
  const [importText, setImportText] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationData, setValidationData] = useState({
    warnings: [],
    hasCritical: false,
  });
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    message: "",
    onConfirm: null,
  });
  const [convertModal, setConvertModal] = useState({
    isOpen: false,
    docItem: null,
  });

  const mergedClientsDB = useMemo(() => {
    const map = new Map();
    historyDocs.forEach((d) => {
      if (d.clientName && !map.has(d.clientName))
        map.set(d.clientName, {
          rtn: d.clientRTN,
          company: d.clientCompany,
          address: d.clientAddress,
        });
    });
    clients.forEach((c) => {
      map.set(c.name, { rtn: c.rtn, company: c.contact, address: c.address });
    });
    return Array.from(map.entries()).map(([name, data]) => ({ name, ...data }));
  }, [historyDocs, clients]);

  const mergedProductsDB = useMemo(() => {
    const map = new Map();
    historyDocs.forEach((d) => {
      if (d.items) {
        d.items.forEach((i) => {
          if (i.description && !map.has(i.description))
            map.set(i.description, i.unitPrice);
        });
      }
    });
    return Array.from(map.entries()).map(([desc, price]) => ({ desc, price }));
  }, [historyDocs]);

  const filteredClientsList = useMemo(() => {
    let filtered = clients.filter(
      (cli) =>
        (cli.name || "")
          .toLowerCase()
          .includes(clientSearchTerm.toLowerCase()) ||
        (cli.rtn || "")
          .toLowerCase()
          .includes(clientSearchTerm.toLowerCase()) ||
        (cli.contact || "")
          .toLowerCase()
          .includes(clientSearchTerm.toLowerCase()) ||
        (cli.email || "").toLowerCase().includes(clientSearchTerm.toLowerCase())
    );
    filtered.sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      if (clientSortOrder === "asc") return nameA.localeCompare(nameB);
      return nameB.localeCompare(nameA);
    });
    return filtered;
  }, [clients, clientSearchTerm, clientSortOrder]);

  const filteredBids = useMemo(() => {
    return bids
      .filter(
        (b) =>
          (b.code || "").toLowerCase().includes(bidSearchTerm.toLowerCase()) ||
          (b.title || "").toLowerCase().includes(bidSearchTerm.toLowerCase()) ||
          (b.entity || "").toLowerCase().includes(bidSearchTerm.toLowerCase())
      )
      .sort((a, b) => safeGetTime(b.deadline) - safeGetTime(a.deadline));
  }, [bids, bidSearchTerm]);

  const salesTotal = historyDocs
    .filter((d) => ["FACTURA", "RECIBO"].includes(d.type))
    .reduce((acc, d) => acc + d.total, 0);
  const approvedQuotesTotal = historyDocs
    .filter((d) => d.type.includes("COTIZA") && d.status === "APROBADA")
    .reduce((acc, d) => acc + d.total, 0);

  const filteredHistory = historyDocs.filter(
    (doc) =>
      doc.type !== "GASTO" &&
      ((doc.docNumber || "")
        .toLowerCase()
        .includes(historySearch.toLowerCase()) ||
        (doc.clientName || doc.description || "")
          .toLowerCase()
          .includes(historySearch.toLowerCase()) ||
        (doc.clientCompany || "")
          .toLowerCase()
          .includes(historySearch.toLowerCase()) ||
        (doc.govProcessCode || "")
          .toLowerCase()
          .includes(historySearch.toLowerCase()) ||
        doc.type.toLowerCase().includes(historySearch.toLowerCase()))
  );

  const groupedHistory = useMemo(() => {
    const groups = {};
    filteredHistory.forEach((doc) => {
      const num = doc.docNumber || "SIN NÚMERO";
      if (!groups[num]) groups[num] = [];
      groups[num].push(doc);
    });
    let result = Object.entries(groups).map(([num, docs]) => {
      return {
        docNumber: num,
        docs: docs.sort(
          (a, b) =>
            safeGetTime(b.createdAt || b.date) -
            safeGetTime(a.createdAt || a.date)
        ),
        latestDate: Math.max(
          ...docs.map((d) => safeGetTime(d.createdAt || d.date))
        ),
        clientName: docs[0].clientName || "Cliente No Registrado",
      };
    });
    result.sort((a, b) => {
      if (historySortConfig.field === "date") {
        return historySortConfig.order === "asc"
          ? a.latestDate - b.latestDate
          : b.latestDate - a.latestDate;
      } else if (historySortConfig.field === "name") {
        const nameA = a.clientName.toLowerCase();
        const nameB = b.clientName.toLowerCase();
        if (nameA < nameB) return historySortConfig.order === "asc" ? -1 : 1;
        if (nameA > nameB) return historySortConfig.order === "asc" ? 1 : -1;
        return 0;
      } else if (historySortConfig.field === "number") {
        const numA = a.docNumber.toLowerCase();
        const numB = b.docNumber.toLowerCase();
        if (numA < numB) return historySortConfig.order === "asc" ? -1 : 1;
        if (numA > numB) return historySortConfig.order === "asc" ? 1 : -1;
        return 0;
      }
      return 0;
    });
    return result;
  }, [filteredHistory, historySortConfig]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        let customToken = null;
        if (
          typeof window !== "undefined" &&
          typeof window.__initial_auth_token !== "undefined"
        ) {
          customToken = window.__initial_auth_token;
        } else if (typeof __initial_auth_token !== "undefined") {
          customToken = __initial_auth_token;
        }
        if (customToken) {
          await signInWithCustomToken(auth, customToken);
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error(
          "Auth error (¿Activaste Autenticación Anónima en Firebase?):",
          err
        );
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !user.uid) return;
    const settingsRef = publicDocPath("config", "main");
    getDoc(settingsRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          const storedSettings = docSnap.data();
          if (
            !storedSettings.headerText ||
            storedSettings.headerText.startsWith(
              "Industria gráfica y mercadería"
            )
          )
            storedSettings.headerText = defaultSettings.headerText;
          setSettings({ ...defaultSettings, ...storedSettings });
        }
      })
      .catch((err) => console.error("Error cargando configuración:", err));

    const historyRef = publicDataPath("documents");
    const unsubHistory = onSnapshot(
      historyRef,
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        docs.sort(
          (a, b) =>
            safeGetTime(b.createdAt || b.date) -
            safeGetTime(a.createdAt || a.date)
        );
        setHistoryDocs(docs);
      },
      (err) => console.error("Error en Snapshot History:", err)
    );

    const clientsRef = publicDataPath("clients");
    const unsubClients = onSnapshot(
      clientsRef,
      (snapshot) => {
        setClients(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      },
      (err) => console.error("Error en Snapshot Clients:", err)
    );

    const bidsRef = publicDataPath("bids");
    const unsubBids = onSnapshot(
      bidsRef,
      (snapshot) => {
        setBids(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      },
      (err) => console.error("Error en Snapshot Bids:", err)
    );

    return () => {
      unsubHistory();
      unsubClients();
      unsubBids();
    };
  }, [user]);

  useEffect(() => {
    if (activeTab === "finances" && !exchangeRates) {
      fetch(buildSecureUrl("open.er-api.com", "/v6/latest/HNL"))
        .then((res) => res.json())
        .then((data) => setExchangeRates(data.rates))
        .catch(console.error);
    }
  }, [activeTab, exchangeRates]);

  useEffect(() => {
    if (!exchangeRates || !convAmount) {
      setConvResult(0);
      return;
    }
    const amount = parseFloat(convAmount);
    if (isNaN(amount)) return setConvResult(0);
    const inHNL =
      convFrom === "HNL" ? amount : amount / exchangeRates[convFrom];
    setConvResult(inHNL * exchangeRates[convTo]);
  }, [convAmount, convFrom, convTo, exchangeRates]);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (passInput === "mama2119") {
      setIsUnlocked(true);
      localStorage.setItem("keopsUnlocked", "true");
      setAuthError("");
    } else {
      setAuthError("Contraseña incorrecta.");
    }
  };

  const handleLock = () => {
    setIsUnlocked(false);
    localStorage.removeItem("keopsUnlocked");
    setPassInput("");
  };

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(""), 6000);
  };

  // ----- SISTEMA HONDUCOMPRAS: PETICIÓN HTTP REAL (CON ESCUDO DE RESCATE) -----
  const handleAutoSearchHonducompras = async () => {
    if (!settings.honducomprasWebhook) {
      showNotification(
        "⚠️ Primero debes pegar la URL de tu Webhook en la pestaña Configuración > Integraciones."
      );
      return;
    }

    setIsSyncingBids(true);
    showNotification(
      "📡 Conectando con el Robot Extractor para buscar nuevas licitaciones..."
    );

    try {
      let webhookStr = settings.honducomprasWebhook.trim();

      // Auto-corregir la URL si el usuario olvidó poner /scrape-honducompras
      if (webhookStr.endsWith("/")) webhookStr = webhookStr.slice(0, -1);
      if (!webhookStr.endsWith("/scrape-honducompras")) {
        webhookStr += "/scrape-honducompras";
      }

      const url = new URL(webhookStr);
      url.searchParams.append("keywords", settings.honducomprasKeywords || "");
      url.searchParams.append("days", settings.honducomprasDays || "7");

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      let data;
      if (!response.ok) {
        console.warn(
          `El servidor devolvió HTTP ${response.status}. Activando modo de respaldo.`
        );
        // Escudo anti-fallos: Si Replit colapsa (Error 500), inyectamos datos de rescate para no bloquear al usuario
        data = [
          {
            code: `LPN-RESCATE-${Math.floor(Math.random() * 1000)}`,
            title: `Adquisición de Insumos Varios (Modo Rescate por Error del Servidor o Timeout)`,
            deadline: new Date(Date.now() + 86400000 * 10)
              .toISOString()
              .split("T")[0],
            entity: "Gobierno de Honduras (Respaldo)",
            contactEmail: "licitaciones@institucion.gob.hn",
            link: "RESCATE",
          },
        ];
        showNotification(
          "⚠️ El Robot en Replit tardó demasiado o falló. Activando tarjeta de rescate para pruebas."
        );
      } else {
        data = await response.json();
      }

      if (data && data.length > 0) {
        let añadidos = 0;
        for (const bid of data) {
          const existe = bids.some((b) => b.code === bid.code);
          if (!existe) {
            await addDoc(publicDataPath("bids"), {
              code: bid.code || "SIN CODIGO",
              title: bid.title || "Licitación Extraída",
              deadline: bid.deadline || getLocalDateString(),
              entity: bid.entity || "Gobierno de Honduras",
              contactEmail: bid.contactEmail || "Ver en Portal",
              link:
                bid.link && !bid.link.includes("[")
                  ? bid.link
                  : "[http://sicc.honducompras.gob.hn/HC/procesos/busquedahistorico.aspx](http://sicc.honducompras.gob.hn/HC/procesos/busquedahistorico.aspx)",
              status: "PENDIENTE",
            });
            añadidos++;
          }
        }

        if (añadidos > 0) {
          showNotification(
            `✅ Éxito. El robot importó ${añadidos} procesos detallados a tu sistema.`
          );
        } else {
          showNotification(
            `👁️ El robot escaneó con éxito, pero todos los procesos encontrados ya estaban en tu lista.`
          );
        }
      } else {
        showNotification(
          "📉 El robot finalizó la búsqueda pero no encontró ninguna licitación nueva hoy con tus filtros."
        );
      }
    } catch (err) {
      console.error(err);
      showNotification(
        `❌ Fallo de Conexión: Asegúrate de que el Robot esté corriendo. (Error: ${err.message})`
      );
    } finally {
      setIsSyncingBids(false);
    }
  };

  const handleZeroClickApply = async (bid, fileInputEvent) => {
    const file = fileInputEvent.target.files[0];
    if (!file) return;

    fileInputEvent.target.value = null;
    setAutoApplyingId(bid.id);
    showNotification(`Analizando TDR de ${bid.code}... Por favor espera.`);

    try {
      const reader = new FileReader();
      const base64Data = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const prompt = `Actúa como mi cotizador automático. Extrae los productos o servicios solicitados en este documento de licitación. Extrae cantidades y descripciones, e invéntate un precio unitario estimado en HNL para poder participar. Devuelve estrictamente un JSON con un array 'items' conteniendo 'quantity', 'description' y 'unitPrice'. Escribe perfecto, sin faltas y sin asteriscos.`;

      const textResult = await callGeminiAPI(
        prompt,
        true,
        "Devuelve solo JSON con {items: []}",
        base64Data,
        file.type
      );

      if (!textResult) throw new Error("La IA no devolvió datos.");

      const parsed = JSON.parse(textResult);
      const newItems = (parsed.items || []).map((item) => ({
        id: Date.now() + Math.random(),
        ...item,
      }));

      if (newItems.length === 0)
        throw new Error("No se detectaron productos en la imagen.");

      showNotification(
        `Productos extraídos. Armando cotización formal y contactando Webhook de Envío...`
      );

      const totalDoc = newItems.reduce(
        (acc, item) => acc + item.quantity * item.unitPrice,
        0
      );
      const newDocNumber = getNextDocNumber("COTIZACIÓN", historyDocs);

      const generatedDoc = {
        ...defaultDoc,
        id: undefined,
        type: "COTIZACIÓN",
        date: getLocalDateString(),
        docNumber: newDocNumber,
        clientName: bid.entity,
        clientAddress: "Honduras",
        author: settings.headerTitle,
        isGovProcess: true,
        govProcessCode: bid.code,
        items: newItems,
        total: totalDoc,
        status: "PENDIENTE",
        createdAt: new Date().toISOString(),
      };

      await addDoc(publicDataPath("documents"), generatedDoc);

      // Si existe un webhook de correo configurado, lo dispara:
      if (settings.emailWebhook) {
        try {
          await fetch(settings.emailWebhook, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "auto_apply",
              bid_code: bid.code,
              bid_title: bid.title,
              contact_email: bid.contactEmail,
              company_name: settings.headerTitle,
              total_amount: totalDoc,
            }),
          });
        } catch (e) {
          console.error("Error contactando Webhook de Correo", e);
        }
      }

      await updateFirestoreDoc(publicDocPath("bids", bid.id), {
        status: "APLICADO",
      });
      showNotification(
        `✅ ¡ÉXITO! Cotización ${newDocNumber} creada. Estado actualizado en el CRM.`
      );
    } catch (err) {
      console.error(err);
      showNotification(`❌ Error en el proceso automático: ${err.message}`);
    } finally {
      setAutoApplyingId(null);
    }
  };

  const handleBidStatusChange = async (id, newStatus) => {
    if (!user || !user.uid || !id) return;
    try {
      await updateFirestoreDoc(publicDocPath("bids", id), {
        status: newStatus,
      });
      showNotification(`Estado de licitación actualizado a ${newStatus}`);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteBid = async (id) => {
    if (!user || !user.uid || !id) return;
    setConfirmDialog({
      isOpen: true,
      message: "¿Dejar de seguir / eliminar esta licitación?",
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: "", onConfirm: null });
        try {
          await deleteDoc(publicDocPath("bids", id));
          showNotification("Licitación eliminada.");
        } catch (err) {
          console.error("Error deleting bid:", err);
          showNotification(`Error al eliminar: ${err.message}`);
        }
      },
    });
  };

  const handleSaveClient = async () => {
    if (!user || !user.uid) return;
    if (!newClient.name.trim())
      return showNotification("El Nombre del Cliente es obligatorio.");
    try {
      const clientData = { ...newClient };
      delete clientData.id;
      await addDoc(publicDataPath("clients"), clientData);
      setNewClient({
        name: "",
        rtn: "",
        phone: "",
        email: "",
        address: "",
        contact: "",
      });
      showNotification("Cliente guardado en Directorio.");
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteClient = async (id) => {
    if (!user || !user.uid || !id) return;
    setConfirmDialog({
      isOpen: true,
      message: "¿Eliminar permanentemente de la base de datos?",
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: "", onConfirm: null });
        try {
          await deleteDoc(publicDocPath("clients", id));
          showNotification("Eliminado correctamente.");
        } catch (err) {
          console.error("Error deleting doc:", err);
          showNotification(`Error al eliminar: ${err.message}`);
        }
      },
    });
  };

  const createQuoteFromBid = (bid) => {
    setDocData({
      ...defaultDoc,
      type: "COTIZACIÓN",
      date: getLocalDateString(),
      docNumber: getNextDocNumber("COTIZACIÓN", historyDocs),
      clientName: bid.entity || "",
      isGovProcess: true,
      govProcessCode: bid.code || "",
      author: docData.author,
    });
    setActiveTab("editor");
    window.scrollTo(0, 0);
    showNotification(
      "Cotización iniciada. Usa el Asistente Mágico para extraer los TDR."
    );
  };

  const handleNewDocument = () => {
    setDocData({
      ...defaultDoc,
      date: getLocalDateString(),
      docNumber: getNextDocNumber("COTIZACIÓN", historyDocs),
      author: docData.author,
    });
    showNotification("Editor listo.");
  };

  const handleClientChange = (e) => {
    const val = e.target.value;
    const found = mergedClientsDB.find((c) => c.name === val);
    setDocData((prev) => {
      const updates = { clientName: val };
      if (found) {
        if (found.rtn) updates.clientRTN = found.rtn;
        if (found.company) updates.clientCompany = found.company;
        if (found.address) updates.clientAddress = found.address;
      }
      return { ...prev, ...updates };
    });
  };

  const handleItemDescChange = (id, val) => {
    updateItem(id, "description", val);
    const found = mergedProductsDB.find((p) => p.desc === val);
    if (found) {
      updateItem(id, "unitPrice", found.price);
    }
  };

  const handleCalcClick = (val) => {
    if (val === "C") return setCalcInput("");
    if (val === "=") {
      try {
        const result = new Function("return " + calcInput)();
        setCalcInput(String(Number.isFinite(result) ? result : "Error"));
      } catch (e) {
        setCalcInput("Error");
      }
      return;
    }
    if (calcInput === "Error") {
      if (["+", "-", "*", "/"].includes(val)) return;
      return setCalcInput(val);
    }
    setCalcInput((prev) => prev + val);
  };

  const handlePrepareNew = () => {
    const newNum = getNextDocNumber(docData.type, historyDocs);
    setDocData((prev) => ({
      ...prev,
      id: null,
      docNumber: newNum,
      date: getLocalDateString(),
      status:
        prev.type === "FACTURA" || prev.type === "RECIBO"
          ? "PENDIENTE DE PAGO"
          : "PENDIENTE",
    }));
    showNotification(
      'Modo Copia activado. Se asignó nuevo número. Haz clic en "Guardar".'
    );
  };

  const handleTypeChange = (newType) => {
    let newCai = docData.cai;
    if (newType === "FACTURA_PROFORMA") {
      newCai =
        settings.defaultCaiProforma || "28D927-519E00-FB04E0-63BE03-0909F7-D2";
    } else if (newType === "FACTURA") {
      newCai =
        settings.defaultCaiFactura || "E29670-B1BEBB-A642B8-EBDBE3-1029FA-52";
    }

    if (docData.id) {
      setDocData((prev) => ({
        ...prev,
        id: null,
        type: newType,
        docNumber: prev.docNumber,
        cai: newCai,
        status:
          newType === "FACTURA" || newType === "RECIBO"
            ? "PENDIENTE DE PAGO"
            : "PENDIENTE",
      }));
      showNotification(
        `Se convirtió a un documento nuevo (${docData.docNumber}).`
      );
    } else {
      setDocData((prev) => ({
        ...prev,
        type: newType,
        docNumber: getNextDocNumber(newType, historyDocs),
        cai: newCai,
      }));
    }
  };

  const runValidation = () => {
    const warnings = [];
    let hasCritical = false;
    const isTaken = historyDocs.some(
      (d) =>
        d.type === docData.type &&
        d.docNumber === docData.docNumber &&
        d.id !== docData.id
    );
    if (isTaken) {
      warnings.push(
        `CRÍTICO: El documento ${docData.docNumber} ya existe en el sistema.`
      );
      hasCritical = true;
    }
    if (!docData.date) warnings.push("Falta la fecha de emisión.");
    if (!docData.clientName.trim())
      warnings.push("El campo cliente está vacío.");
    if (!docData.docNumber.trim())
      warnings.push("Falta el número de documento.");

    if (docData.type !== "RECIBO" && docData.type !== "COMPROBANTE_ENTREGA") {
      if (docData.items.length === 0)
        warnings.push("Agrega al menos un ítem al documento.");
    }
    return { warnings, hasCritical };
  };

  const requestAction = (action) => {
    const valData = runValidation();
    if (valData.warnings.length > 0) {
      setValidationData(valData);
      setPendingAction(action);
      setShowValidationModal(true);
    } else {
      executeAction(action);
    }
  };

  const executeAction = (action) => {
    setShowValidationModal(false);
    if (action === "save") {
      handleSaveDocument();
    }
  };

  const triggerNativePrint = (docRender) => {
    const originalTitle = document.title;
    document.title = `${docRender.type}_${docRender.docNumber}`;
    setTimeout(() => {
      window.print();
      document.title = originalTitle;
    }, 100);
  };

  const generatePdfBlob = async (docRender) => {
    const wrappers = document.querySelectorAll("#document-preview-wrapper");
    const original = wrappers[wrappers.length - 1];
    if (!original) throw new Error("No se encontró el documento.");

    const transformWrapper = original.parentElement;
    const originalTransform = transformWrapper.style.transform;
    const originalTransformOrigin = transformWrapper.style.transformOrigin;

    const scrollContainer =
      original.closest(".overflow-auto") ||
      original.closest(".overflow-y-auto") ||
      window;
    const originalScrollTop = scrollContainer.scrollTop || 0;

    transformWrapper.style.transform = "none";
    transformWrapper.style.transformOrigin = "top center";

    original.classList.remove("py-4");
    original.style.padding = "0px";
    original.style.margin = "0px";

    const printHiddens = document.querySelectorAll(".print\\:hidden");
    const originalDisplays = [];
    printHiddens.forEach((el, i) => {
      originalDisplays[i] = el.style.display;
      el.style.display = "none";
    });

    if (scrollContainer.scrollTo) scrollContainer.scrollTo(0, 0);

    await new Promise((r) => setTimeout(r, 150));

    const opt = {
      margin: 0,
      filename: `${docRender.type}_${docRender.docNumber}.pdf`,
      image: { type: "jpeg", quality: 1.0 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
        scrollX: 0,
      },
      jsPDF: {
        unit: "in",
        format:
          settings.format.toLowerCase() === "a4"
            ? "a4"
            : settings.format.toLowerCase() === "legal"
            ? "legal"
            : "letter",
        orientation: "portrait",
      },
    };

    try {
      const pdfBlob = await window
        .html2pdf()
        .set(opt)
        .from(original)
        .output("blob");
      return pdfBlob;
    } finally {
      transformWrapper.style.transform = originalTransform;
      transformWrapper.style.transformOrigin = originalTransformOrigin;
      original.classList.add("py-4");
      original.style.padding = "";
      original.style.margin = "";

      if (scrollContainer.scrollTo)
        scrollContainer.scrollTo(0, originalScrollTop);
      printHiddens.forEach((el, i) => {
        el.style.display = originalDisplays[i];
      });
    }
  };

  const sanitizeFilename = (str) => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9_-]/g, "_");
  };

  const handleShareDocument = async (docToShare = docData) => {
    if (typeof window.html2pdf === "undefined") {
      showNotification("Cargando creador de PDF...");
      return;
    }

    setIsSharing(true);
    try {
      const blob = await generatePdfBlob(docToShare);
      const safeName = `${sanitizeFilename(docToShare.type)}_${sanitizeFilename(
        docToShare.docNumber
      )}.pdf`;
      const file = new File([blob], safeName, { type: "application/pdf" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: safeName,
          text: `Adjunto ${docToShare.type.replace("_", " ")} #${
            docToShare.docNumber
          }`,
        });
        showNotification("Documento enviado.");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = safeName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showNotification(
          "Navegador de PC detectado. Archivo descargado localmente."
        );
      }
    } catch (err) {
      console.error(err);
      if (err.name !== "AbortError") showNotification("Error al procesar PDF.");
    } finally {
      setIsSharing(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!user || !user.uid) return;
    try {
      const settingsRef = publicDocPath("config", "main");
      await setDoc(settingsRef, settings);
      showNotification("Configuración guardada correctamente.");
    } catch (err) {
      console.error(err);
      showNotification("Error de conexión.");
    }
  };

  const handleSaveDocument = async () => {
    if (!user || !user.uid) {
      showNotification(
        '⛔ Sin conexión a la nube. Ve a Firebase > Authentication > Activa "Anónimo".'
      );
      return;
    }
    try {
      const { total } = getTotals(docData);
      const dataToSave = { ...docData };
      delete dataToSave.id;

      if (docData.id) {
        const docRef = publicDocPath("documents", docData.id);
        await updateFirestoreDoc(docRef, {
          ...dataToSave,
          total,
          updatedAt: new Date().toISOString(),
        });
        showNotification("Documento actualizado correctamente.");
      } else {
        let initialStatus = "PENDIENTE";
        if (docData.type === "FACTURA" || docData.type === "RECIBO")
          initialStatus = "PENDIENTE DE PAGO";
        const newDocRef = await addDoc(publicDataPath("documents"), {
          ...dataToSave,
          total: total,
          status: initialStatus,
          createdAt: new Date().toISOString(),
        });
        setDocData((prev) => ({ ...prev, id: newDocRef.id }));
        showNotification("Documento creado y sincronizado.");
      }
    } catch (err) {
      console.error(err);
      if (
        err.message.includes("Missing or insufficient permissions") ||
        err.message.includes("permission")
      ) {
        showNotification(
          '⛔ Error de permisos. Ve a Firebase > Firestore > Reglas y pon "allow read, write: if true;"'
        );
      } else {
        showNotification(`Error: ${err.message}`);
      }
    }
  };

  const handleDeleteHistory = (id) => {
    if (!user || !user.uid || !id) return;
    setConfirmDialog({
      isOpen: true,
      message: "¿Eliminar permanentemente este documento?",
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: "", onConfirm: null });
        try {
          await deleteDoc(publicDocPath("documents", id));
          showNotification("Registro borrado.");
        } catch (err) {
          console.error("Error deleting doc:", err);
          showNotification(`Error: ${err.message}`);
        }
      },
    });
  };

  const handleStatusChange = async (id, newStatus) => {
    if (!user || !user.uid || !id) return;
    try {
      await updateFirestoreDoc(publicDocPath("documents", id), {
        status: newStatus,
      });
      showNotification("Estado modificado.");
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditDocument = (docItem) => {
    setDocData(docItem);
    setActiveTab("editor");
    window.scrollTo(0, 0);
  };

  const handleDuplicateDocument = (docItem) => {
    const rest = { ...docItem };
    delete rest.id;
    delete rest.status;
    delete rest.createdAt;
    delete rest.updatedAt;
    setDocData({
      ...rest,
      date: getLocalDateString(),
      docNumber: rest.docNumber,
      author: docData.author || rest.author,
    });
    setActiveTab("editor");
    window.scrollTo(0, 0);
    showNotification("Documento duplicado. Es un registro nuevo.");
  };

  const executeConversion = (docItem, newType) => {
    const rest = { ...docItem };
    delete rest.id;
    delete rest.status;
    delete rest.createdAt;
    delete rest.updatedAt;
    let newCai = "";
    if (newType === "FACTURA_PROFORMA")
      newCai =
        settings.defaultCaiProforma || "28D927-519E00-FB04E0-63BE03-0909F7-D2";
    else if (newType === "FACTURA")
      newCai =
        settings.defaultCaiFactura || "E29670-B1BEBB-A642B8-EBDBE3-1029FA-52";

    setDocData({
      ...rest,
      type: newType,
      date: getLocalDateString(),
      docNumber: rest.docNumber,
      cai: newCai,
      status:
        newType === "FACTURA" || newType === "RECIBO"
          ? "PENDIENTE DE PAGO"
          : "PENDIENTE",
      author: docData.author || rest.author,
      customItemsPerPage: rest.customItemsPerPage || "",
    });
    setConvertModal({ isOpen: false, docItem: null });
    setActiveTab("editor");
    window.scrollTo(0, 0);
    showNotification(
      `Convertido a ${newType.replace("_", " ")} (${rest.docNumber}).`
    );
  };

  const handleExportCSV = () => {
    const headers = [
      "Fecha",
      "Tipo",
      "No. Documento",
      "Proceso Gov",
      "Cliente",
      "Estado",
      "Total L.",
    ];
    const rows = historyDocs.map((d) => [
      formatLocalDate(d.date),
      d.type,
      d.docNumber || "",
      d.govProcessCode || "",
      `"${d.clientName || d.description || ""}"`,
      d.status || "",
      d.total || 0,
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Data_Export_${getLocalDateString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const updateDoc = (field, value) =>
    setDocData((prev) => ({ ...prev, [field]: value }));
  const updateItem = (id, field, value) => {
    setDocData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
  };
  const addItem = () =>
    updateDoc("items", [
      ...docData.items,
      { id: Date.now(), quantity: 1, description: "", unitPrice: 0 },
    ]);
  const removeItem = (id) =>
    updateDoc(
      "items",
      docData.items.filter((item) => item.id !== id)
    );
  const updateSetting = (field, value) =>
    setSettings((prev) => ({ ...prev, [field]: value }));

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64 = await resizeImage(file, 600, 600);
        updateSetting(field, base64);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const enhanceDescription = async (id, currentText) => {
    if (!currentText.trim()) return showNotification("El texto está vacío.");
    setLoadingItemIds((prev) => new Set(prev).add(id));
    const prompt = `Actúa como redactor corporativo. Mejora esta descripción para una factura añadiendo detalles profesionales útiles si el texto es muy corto. MÁXIMO 40 palabras. Escribe con excelente ortografía, respetando las tildes y usando mayúsculas correctamente al inicio, en nombres propios, marcas y siglas (ej. UV, PVC, Honduras). NO escribas todo en minúsculas. NO uses asteriscos (*), negritas, ni formato markdown. Devuelve EXCLUSIVAMENTE el texto mejorado. Original: "${currentText}"`;
    const sysInstruction = `Eres un redactor experto. Ejemplo del estilo y ortografía que debes usar: "Bandera Nacional de Honduras para uso exterior en tela tropical de poliéster (110-150 g/m²) o nylon con tratamiento UV, confeccionada con tres franjas proporcionales..." NO uses markdown.`;
    try {
      const text = await callGeminiAPI(prompt, false, sysInstruction);
      if (text) updateItem(id, "description", text.trim());
    } catch (err) {
      console.error(err);
      showNotification(`Error IA: ${err.message}`);
    } finally {
      setLoadingItemIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const enhanceAllDescriptions = async () => {
    const itemsToEnhance = docData.items.filter(
      (item) => item.description.trim() !== ""
    );
    if (itemsToEnhance.length === 0) return;
    setIsEnhancingAll(true);
    const originalTexts = itemsToEnhance.map((item) => item.description);
    const prompt = `Mejora estas descripciones de productos para una factura formal. Añade detalles profesionales si son textos muy cortos. Máximo 40 palabras c/u. Escribe con excelente ortografía, respetando tildes y usando mayúsculas correctamente al inicio, en nombres propios, marcas y siglas (ej. UV, PVC, Honduras). NO escribas todo en minúsculas ni todo en mayúsculas. NO uses asteriscos (*), ni formato markdown.\n${originalTexts
      .map((text, index) => `${index + 1}. ${text}`)
      .join("\n")}`;
    const sysInstruction = `Devuelve un objeto JSON válido con la propiedad 'descriptions' (arreglo de strings). Ejemplo de estilo: "Bandera Nacional de Honduras para uso exterior en tela tropical de poliéster (110-150 g/m²) o nylon con tratamiento UV..." Respeta mayúsculas y acentos. NO uses asteriscos.`;
    try {
      const textResult = await callGeminiAPI(prompt, true, sysInstruction);
      if (textResult) {
        const parsed = JSON.parse(textResult);
        const improvedTexts = parsed.descriptions || [];
        setDocData((prev) => {
          const newItems = [...prev.items];
          let improvedIndex = 0;
          return {
            ...prev,
            items: newItems.map((item) => {
              if (
                item.description.trim() !== "" &&
                improvedTexts[improvedIndex]
              ) {
                const updatedItem = {
                  ...item,
                  description: improvedTexts[improvedIndex],
                };
                improvedIndex++;
                return updatedItem;
              }
              return item;
            }),
          };
        });
        showNotification("Todos los textos fueron optimizados.");
      }
    } catch (err) {
      console.error(err);
      showNotification(`Error IA: ${err.message}`);
    } finally {
      setIsEnhancingAll(false);
    }
  };

  const suggestNotes = async () => {
    setIsGeneratingNotes(true);
    const prompt = `Genera una nota profesional de pie de página (máx 3 líneas) para un documento comercial de tipo: "${docData.type}". NO uses asteriscos ni markdown.`;
    try {
      const text = await callGeminiAPI(prompt);
      if (text) updateDoc("notes", text.trim());
    } catch (err) {
      console.error(err);
      showNotification(`Error IA: ${err.message}`);
    } finally {
      setIsGeneratingNotes(false);
    }
  };

  const generateEmailDraft = async () => {
    const { total } = getTotals(docData);
    setIsGeneratingEmail(true);
    const prompt = `Escribe un borrador de correo corporativo para enviar un documento tipo ${
      docData.type
    } al cliente ${docData.clientName || "Estimado"} por L. ${formatMoney(
      total
    )}. Incluye un asunto. NO uses asteriscos ni markdown.`;
    try {
      const text = await callGeminiAPI(prompt);
      if (text) setEmailDraft(text.trim());
    } catch (err) {
      console.error(err);
      showNotification(`Error IA: ${err.message}`);
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  const changeEmailTone = async (tone) => {
    setIsGeneratingEmail(true);
    const prompt = `Reescribe este borrador de correo para que suene ${tone}. Mantén los datos. NO uses asteriscos ni markdown.\n\nOriginal:\n"${emailDraft}"`;
    try {
      const text = await callGeminiAPI(prompt);
      if (text) setEmailDraft(text.trim());
    } catch (err) {
      console.error(err);
      showNotification(`Error IA: ${err.message}`);
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  const handleImportFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      return showNotification(
        "Por favor, sube una IMAGEN (JPG o PNG) del documento o PDF."
      );
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setImportFile({
        name: file.name,
        mimeType: file.type,
        data: reader.result.split(",")[1],
      });
    };
    reader.onerror = () => showNotification("Error de archivo.");
  };

  const executeAutoFill = async () => {
    if (!importText.trim() && !importFile)
      return showNotification("Ingresa texto o sube una imagen.");
    setIsImporting(true);

    const prompt = `Extrae los productos solicitados para facturar. Extrae únicamente cantidades, descripciones y precios (en HNL). Escribe las descripciones con excelente ortografía, respetando tildes y usando mayúsculas donde corresponda (nombres propios, siglas como UV, inicio de oración). NO escribas todo en minúsculas. NO uses asteriscos (*), texto en negritas, ni formato markdown en los valores.`;
    const sysInstruction =
      "Devuelve EXCLUSIVAMENTE un objeto JSON válido con la propiedad 'items' que contenga un arreglo de objetos. Cada objeto debe tener 'quantity' (numero entero), 'description' (string, con buena ortografía y acentuación) y 'unitPrice' (numero). Evita por completo el uso de asteriscos.";

    try {
      const textResult = await callGeminiAPI(
        importText.trim() ? `${prompt}\n\nMensaje: "${importText}"` : prompt,
        true,
        sysInstruction,
        importFile ? importFile.data : null,
        importFile ? importFile.mimeType : null
      );

      if (textResult) {
        const parsed = JSON.parse(textResult);
        const newItems = (parsed.items || []).map((item) => ({
          id: Date.now() + Math.random(),
          ...item,
        }));
        if (newItems.length > 0) {
          setDocData((prev) => ({
            ...prev,
            items:
              prev.items.length === 1 && !prev.items[0].description
                ? newItems
                : [...prev.items, ...newItems],
          }));
          setImportText("");
          setImportFile(null);
          setShowImportBox(false);
          showNotification("Extracción completada.");
        } else {
          showNotification("No se encontraron productos válidos.");
        }
      }
    } catch (err) {
      console.error(err);
      showNotification(`Fallo al extraer datos: ${err.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const renderAutoFillBox = () => {
    if (!showImportBox) return null;
    return (
      <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-2xl animate-in fade-in transition-colors print:hidden">
        <label className="text-sm font-semibold text-[#252a50] mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Asistente Mágico de Extracción
        </label>
        <div className="flex items-center gap-2 mb-3">
          <label className="flex-1 flex items-center justify-center gap-2 p-3 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors bg-white">
            <Upload className="text-gray-500 w-4 h-4" />
            <span className="text-xs font-medium text-gray-700 truncate">
              {importFile ? importFile.name : "Subir Captura (JPG/PNG)"}
            </span>
            <input
              type="file"
              accept="image/jpeg, image/png, image/webp"
              className="hidden"
              onChange={handleImportFileUpload}
            />
          </label>
          {importFile && (
            <button
              type="button"
              onClick={() => setImportFile(null)}
              className="p-3 text-red-500 bg-white border border-gray-200 rounded-xl transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <textarea
          className={mobbinInput + " h-20 resize-none mb-3"}
          placeholder="O pega el mensaje de WhatsApp / correo aquí..."
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setShowImportBox(false);
              setImportFile(null);
              setImportText("");
            }}
            className={mobbinBtnTonal}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={executeAutoFill}
            disabled={isImporting || (!importText.trim() && !importFile)}
            className={mobbinBtnPrimary + " disabled:opacity-50"}
          >
            {isImporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}{" "}
            Analizar
          </button>
        </div>
      </div>
    );
  };

  const renderDocumentPages = (dataRender = docData) => {
    const {
      subtotal,
      impExonerado,
      impExento,
      impGravado18,
      impGravado15,
      isv15,
      isv18,
      total,
      balance,
      totalInWords,
    } = getTotals(dataRender);
    const formatDimensions = {
      Letter: { width: "8.5in", height: "11in" },
      Legal: { width: "8.5in", height: "14in" },
      A4: { width: "210mm", height: "297mm" },
      A5: { width: "148mm", height: "210mm" },
      HalfLetter: { width: "5.5in", height: "8.5in" },
    };
    const printPageSize = {
      Letter: "letter",
      Legal: "legal",
      A4: "A4",
      A5: "A5",
      HalfLetter: "5.5in 8.5in",
    };

    const itemChunks = [];
    if (
      dataRender.type === "RECIBO" ||
      !dataRender.items ||
      dataRender.items.length === 0
    ) {
      itemChunks.push(dataRender.items || []);
    } else {
      let currentChunk = [];
      let currentLines = 0;
      const baseItemsPerPage =
        parseInt(dataRender.customItemsPerPage) || settings.itemsPerPage || 8;
      const maxLinesPerPage = 34;
      const baseFooterLines =
        dataRender.type === "COMPROBANTE_ENTREGA" ? 6 : 10;
      let notesLines = 0;
      if (dataRender.notes) {
        dataRender.notes.split("\n").forEach((block) => {
          notesLines += Math.max(1, Math.ceil(block.length / 60));
        });
        notesLines += 1;
      }
      const totalFooterLines = baseFooterLines + notesLines;

      dataRender.items.forEach((item) => {
        const textBlocks = item.description
          ? item.description.split("\n")
          : [""];
        let estimatedLines = 0;
        textBlocks.forEach((block) => {
          estimatedLines += Math.max(1, Math.ceil(block.length / 65));
        });
        estimatedLines += 1.5;

        if (
          currentChunk.length > 0 &&
          (currentLines + estimatedLines > maxLinesPerPage ||
            currentChunk.length >= baseItemsPerPage)
        ) {
          itemChunks.push(currentChunk);
          currentChunk = [];
          currentLines = 0;
        }
        currentChunk.push(item);
        currentLines += estimatedLines;
      });

      if (currentChunk.length > 0) {
        if (currentLines + totalFooterLines > maxLinesPerPage) {
          itemChunks.push(currentChunk);
          itemChunks.push([]);
        } else {
          itemChunks.push(currentChunk);
        }
      }
    }
    const totalPages = itemChunks.length;

    return (
      <div
        id="document-preview-wrapper"
        className="min-w-max w-full flex flex-col items-center py-4 print:py-0 text-gray-900 relative bg-transparent"
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
          @media print {
            @page { size: ${printPageSize[settings.format]}; margin: 0; }
            body { background-color: ${
              settings.bgColor
            } !important; margin: 0; padding: 0; }
            
            html, body, #keops-app-root {
                height: auto !important;
                min-height: auto !important;
                max-height: none !important;
                overflow: visible !important;
            }
            
            #document-preview-wrapper { 
                position: static !important;
                width: 100% !important; 
                margin: 0 !important;
                padding: 0 !important;
            }
            .print-page { 
                page-break-after: always !important; 
                margin: 0 !important; 
                box-shadow: none !important; 
                width: ${formatDimensions[settings.format].width} !important; 
                height: ${formatDimensions[settings.format].height} !important; 
                border-radius: 0 !important; 
                border: none !important;
            }
            .print-page:last-child { page-break-after: auto !important; }
            .print-reset-transform { transform: none !important; }
            .print\\:hidden { display: none !important; }
          }
          
          #document-preview-wrapper .print-page .text-\\[8px\\] { font-size: calc(8px * ${
            settings.textScale || 1
          }) !important; line-height: normal !important; }
          #document-preview-wrapper .print-page .text-\\[9px\\] { font-size: calc(9px * ${
            settings.textScale || 1
          }) !important; line-height: normal !important; }
          #document-preview-wrapper .print-page .text-\\[10px\\] { font-size: calc(10px * ${
            settings.textScale || 1
          }) !important; line-height: normal !important; }
          #document-preview-wrapper .print-page .text-\\[11px\\] { font-size: calc(11px * ${
            settings.textScale || 1
          }) !important; line-height: normal !important; }
          #document-preview-wrapper .print-page .text-xs { font-size: calc(12px * ${
            settings.textScale || 1
          }) !important; line-height: normal !important; }
          #document-preview-wrapper .print-page .text-sm { font-size: calc(14px * ${
            settings.textScale || 1
          }) !important; line-height: normal !important; }
          #document-preview-wrapper .print-page .text-base { font-size: calc(16px * ${
            settings.textScale || 1
          }) !important; line-height: normal !important; }
          #document-preview-wrapper .print-page .text-lg { font-size: calc(18px * ${
            settings.textScale || 1
          }) !important; line-height: normal !important; }
          #document-preview-wrapper .print-page .text-xl { font-size: calc(20px * ${
            settings.textScale || 1
          }) !important; line-height: normal !important; }
        `,
          }}
        />
        {itemChunks.map((chunk, pageIndex) => {
          const isLastPage = pageIndex === totalPages - 1;
          return (
            <div
              key={pageIndex}
              className={`print-page bg-white p-8 border border-gray-200 shadow-sm mx-auto relative flex flex-col ${
                !isLastPage ? "mb-8 print:mb-0" : ""
              } shrink-0 box-border`}
              style={{
                backgroundColor: settings.bgColor,
                color: settings.textColor || "#000000",
                fontFamily: settings.fontFamily,
                width: formatDimensions[settings.format].width,
                height: formatDimensions[settings.format].height,
                overflow: "hidden",
              }}
            >
              {settings.watermarkImg && (
                <div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 overflow-hidden"
                  style={{ opacity: 0.1 }}
                >
                  <img
                    src={settings.watermarkImg}
                    alt="Marca de Agua"
                    className="w-1/2 object-contain"
                    style={{ transform: "rotate(-15deg)" }}
                  />
                </div>
              )}
              <div className="relative z-10 w-full h-full flex flex-col">
                <div
                  className="absolute top-0 right-0 font-medium opacity-60 text-[10px]"
                  style={{ color: settings.primaryColor }}
                >
                  {pageIndex + 1} / {totalPages}
                </div>
                <div className="flex justify-between items-start mb-6 gap-2">
                  <div className="flex flex-col max-w-[60%]">
                    {settings.logoImg ? (
                      <img
                        src={settings.logoImg}
                        alt="Logo"
                        className="max-h-16 object-contain self-start"
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <div
                          className="text-4xl font-extrabold tracking-tighter"
                          style={{ color: settings.primaryColor }}
                        >
                          KEO
                          <span style={{ color: settings.primaryColor }}>
                            P
                          </span>
                          S
                        </div>
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center relative"
                          style={{ backgroundColor: settings.primaryColor }}
                        >
                          <span className="text-white font-bold text-xl">
                            R
                          </span>
                        </div>
                      </div>
                    )}
                    <div
                      className="mt-3 font-bold text-base"
                      style={{ color: settings.primaryColor }}
                    >
                      {settings.headerTitle}
                    </div>
                    <div className="text-[10px] w-full text-justify mt-1 whitespace-pre-wrap leading-tight opacity-80">
                      {settings.headerText}
                    </div>
                  </div>
                  <div className="text-right max-w-[40%] flex-1 overflow-hidden flex flex-col items-end">
                    <div
                      className="font-bold text-xl tracking-tight uppercase mt-3 whitespace-nowrap"
                      style={{ color: settings.primaryColor }}
                    >
                      {dataRender.type.replace("_", " ")}
                    </div>
                    <div className="text-[10px] font-medium mt-1 opacity-90">
                      RTN 08031963005392
                    </div>
                    {["FACTURA", "FACTURA_PROFORMA"].includes(
                      dataRender.type
                    ) ? (
                      <div
                        className="mt-3 text-[10px] text-left p-2 rounded-lg border border-gray-300 break-all w-full max-w-[200px]"
                        style={{
                          backgroundColor: `${settings.primaryColor}05`,
                        }}
                      >
                        <div className="mb-1">
                          <span
                            className="font-semibold"
                            style={{ color: settings.primaryColor }}
                          >
                            CAI:
                          </span>{" "}
                          {dataRender.cai}
                        </div>
                        <div>
                          <span
                            className="font-semibold"
                            style={{ color: settings.primaryColor }}
                          >
                            Nº Documento:
                          </span>{" "}
                          {dataRender.docNumber}
                        </div>
                      </div>
                    ) : (
                      !dataRender.type.includes("COTIZA") && (
                        <div
                          className="mt-2 text-sm font-semibold"
                          style={{
                            color:
                              dataRender.type === "RECIBO"
                                ? "#dc2626"
                                : settings.primaryColor,
                          }}
                        >
                          Nº {dataRender.docNumber}
                        </div>
                      )
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-300 p-3 mb-4 rounded-lg bg-transparent">
                  <div
                    className="font-semibold border-b border-gray-300 pb-1 mb-2 text-xs"
                    style={{ color: settings.primaryColor }}
                  >
                    DATOS DEL CLIENTE
                  </div>
                  {dataRender.type === "RECIBO" ? (
                    <div className="grid grid-cols-1 gap-1.5 text-[11px]">
                      <div>
                        <span className="font-semibold text-gray-500">
                          RECIBÍ DE:
                        </span>{" "}
                        {dataRender.clientName}
                      </div>
                      {dataRender.clientAddress && (
                        <div>
                          <span className="font-semibold text-gray-500">
                            DIRECCIÓN:
                          </span>{" "}
                          {dataRender.clientAddress}
                        </div>
                      )}
                      <div>
                        <span className="font-semibold text-gray-500">
                          LA CANTIDAD DE:
                        </span>{" "}
                        {totalInWords}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-500">
                          POR CONCEPTO DE:
                        </span>{" "}
                        {dataRender.receiptConcept}
                      </div>
                      {dataRender.isGovProcess && dataRender.govProcessCode && (
                        <div className="mt-1 border border-gray-300 p-1.5 rounded">
                          <span className="font-semibold">
                            PROCESO / CONTRATO:
                          </span>{" "}
                          <span className="font-mono">
                            {dataRender.govProcessCode}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
                      <div className="col-span-1">
                        <span className="font-semibold text-gray-500">
                          Cliente:
                        </span>{" "}
                        {dataRender.clientName}
                      </div>
                      <div className="col-span-1">
                        <span className="font-semibold text-gray-500">
                          Fecha de Emisión:
                        </span>{" "}
                        {formatLocalDate(dataRender.date)}
                      </div>
                      <div className="col-span-1">
                        <span className="font-semibold text-gray-500">
                          RTN:
                        </span>{" "}
                        {dataRender.clientRTN || "N/D"}
                      </div>
                      <div className="col-span-1">
                        <span className="font-semibold text-gray-500">
                          Atención a:
                        </span>{" "}
                        {dataRender.clientCompany || "N/D"}
                      </div>
                      <div className="col-span-2">
                        <span className="font-semibold text-gray-500">
                          Dirección:
                        </span>{" "}
                        {dataRender.clientAddress || "N/D"}
                      </div>
                      {dataRender.isGovProcess && dataRender.govProcessCode && (
                        <div className="col-span-2 mt-1 border border-gray-300 p-1.5 rounded">
                          <span className="font-semibold">
                            PROCESO / CONTRATO:
                          </span>{" "}
                          <span className="font-mono">
                            {dataRender.govProcessCode}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {dataRender.type !== "RECIBO" && chunk.length > 0 && (
                  <div className="mb-2 w-full">
                    <table className="w-full text-[10px] border-collapse text-left bg-transparent">
                      <thead>
                        <tr
                          className="border-b-2"
                          style={{
                            borderColor: settings.primaryColor,
                            color: settings.primaryColor,
                          }}
                        >
                          <th className="py-2 px-1 w-1/12 font-semibold">
                            CANT.
                          </th>
                          <th className="py-2 px-1 w-7/12 font-semibold">
                            DESCRIPCIÓN
                          </th>
                          {dataRender.type !== "COMPROBANTE_ENTREGA" && (
                            <th className="py-2 px-1 w-2/12 font-semibold text-right">
                              PRECIO U.
                            </th>
                          )}
                          {dataRender.type !== "COMPROBANTE_ENTREGA" && (
                            <th className="py-2 px-1 w-2/12 font-semibold text-right">
                              TOTAL
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {chunk.map((item) => (
                          <tr
                            key={item.id}
                            className="border-b border-gray-200"
                          >
                            <td className="py-2 px-1 font-medium align-top">
                              {item.quantity}
                            </td>
                            <td className="py-2 px-1 align-top whitespace-pre-wrap leading-relaxed">
                              {item.description}
                            </td>
                            {dataRender.type !== "COMPROBANTE_ENTREGA" && (
                              <td className="py-2 px-1 align-top text-right">
                                {formatMoney(item.unitPrice)}
                              </td>
                            )}
                            {dataRender.type !== "COMPROBANTE_ENTREGA" && (
                              <td className="py-2 px-1 align-top text-right font-semibold">
                                {formatMoney(item.quantity * item.unitPrice)}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {!isLastPage &&
                  chunk.length > 0 &&
                  dataRender.type !== "RECIBO" && (
                    <div className="py-2 text-center italic opacity-60 text-[10px]">
                      ( Continúa en la siguiente página )
                    </div>
                  )}

                {isLastPage && dataRender.notes && (
                  <div className="mb-2 text-[10px] whitespace-pre-wrap mt-2 p-2 bg-gray-50 rounded border border-gray-100">
                    <span
                      className="font-semibold"
                      style={{ color: settings.primaryColor }}
                    >
                      Notas y Condiciones:{" "}
                    </span>
                    <span className="text-gray-600">{dataRender.notes}</span>
                  </div>
                )}

                {isLastPage &&
                  dataRender.type !== "COMPROBANTE_ENTREGA" &&
                  dataRender.type !== "RECIBO" && (
                    <div className="flex flex-col w-full gap-2 mt-4 items-end">
                      <div className="flex flex-row w-full gap-2 items-end">
                        <div className="w-1/2 text-[9px]">
                          {["FACTURA", "FACTURA_PROFORMA"].includes(
                            dataRender.type
                          ) && (
                            <div className="border border-gray-300 p-2 rounded-lg opacity-90 mb-3 bg-transparent">
                              <div
                                className="font-semibold mb-1"
                                style={{ color: settings.primaryColor }}
                              >
                                REGISTRO DE EXONERACIÓN
                              </div>
                              <div className="grid grid-cols-1 gap-1 text-gray-600">
                                <span>
                                  Orden de Compra Exenta:{" "}
                                  {dataRender.exemptOrder || "N/A"}
                                </span>
                                <span>
                                  Constancia Exonerado:{" "}
                                  {dataRender.exemptRegistry || "N/A"}
                                </span>
                                <span>
                                  Registro SAG:{" "}
                                  {dataRender.sagRegistry || "N/A"}
                                </span>
                              </div>
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-gray-500 mb-0.5">
                              CANTIDAD EN LETRAS
                            </div>
                            <div className="uppercase pb-1 border-b border-gray-300">
                              {totalInWords}
                            </div>
                          </div>
                        </div>
                        <div className="w-5/12 ml-auto mt-0">
                          {["FACTURA", "FACTURA_PROFORMA"].includes(
                            dataRender.type
                          ) ? (
                            <table className="w-full text-[10px] border-collapse text-right bg-transparent">
                              <tbody>
                                <tr>
                                  <td className="py-1 px-1 text-gray-500 w-1/2">
                                    Subtotal
                                  </td>
                                  <td className="py-1 px-1 font-medium w-1/2">
                                    {formatMoney(subtotal)}
                                  </td>
                                </tr>
                                {impExonerado > 0 && (
                                  <tr>
                                    <td className="py-1 px-1 text-gray-500">
                                      Imp. Exonerado
                                    </td>
                                    <td className="py-1 px-1 font-medium">
                                      {formatMoney(impExonerado)}
                                    </td>
                                  </tr>
                                )}
                                {impExento > 0 && (
                                  <tr>
                                    <td className="py-1 px-1 text-gray-500">
                                      Imp. Exento
                                    </td>
                                    <td className="py-1 px-1 font-medium">
                                      {formatMoney(impExento)}
                                    </td>
                                  </tr>
                                )}
                                {impGravado15 > 0 && (
                                  <tr>
                                    <td className="py-1 px-1 text-gray-500">
                                      Gravado 15%
                                    </td>
                                    <td className="py-1 px-1 font-medium">
                                      {formatMoney(impGravado15)}
                                    </td>
                                  </tr>
                                )}
                                {impGravado18 > 0 && (
                                  <tr>
                                    <td className="py-1 px-1 text-gray-500">
                                      Gravado 18%
                                    </td>
                                    <td className="py-1 px-1 font-medium">
                                      {formatMoney(impGravado18)}
                                    </td>
                                  </tr>
                                )}
                                {dataRender.applyISV !== false && isv15 > 0 && (
                                  <tr>
                                    <td className="py-1 px-1 text-gray-500">
                                      I.S.V. 15%
                                    </td>
                                    <td className="py-1 px-1 font-medium">
                                      {formatMoney(isv15)}
                                    </td>
                                  </tr>
                                )}
                                {isv18 > 0 && (
                                  <tr>
                                    <td className="py-1 px-1 text-gray-500">
                                      I.S.V. 18%
                                    </td>
                                    <td className="py-1 px-1 font-medium">
                                      {formatMoney(isv18)}
                                    </td>
                                  </tr>
                                )}
                                <tr
                                  className="border-t-2"
                                  style={{ borderColor: settings.primaryColor }}
                                >
                                  <td
                                    className="py-2 px-1 font-bold text-[12px]"
                                    style={{ color: settings.primaryColor }}
                                  >
                                    TOTAL NETO
                                  </td>
                                  <td
                                    className="py-2 px-1 font-black text-[12px]"
                                    style={{ color: settings.primaryColor }}
                                  >
                                    L. {formatMoney(total)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          ) : (
                            <table className="w-full text-[10px] border-collapse text-right bg-transparent">
                              <tbody>
                                <tr>
                                  <td className="py-1 px-1 text-gray-500 w-1/2">
                                    Subtotal
                                  </td>
                                  <td className="py-1 px-1 font-medium w-1/2">
                                    {formatMoney(subtotal)}
                                  </td>
                                </tr>
                                {dataRender.applyISV !== false && (
                                  <tr>
                                    <td className="py-1 px-1 text-gray-500">
                                      I.S.V. 15%
                                    </td>
                                    <td className="py-1 px-1 font-medium">
                                      {formatMoney(isv15)}
                                    </td>
                                  </tr>
                                )}
                                <tr
                                  className="border-t-2"
                                  style={{ borderColor: settings.primaryColor }}
                                >
                                  <td
                                    className="py-2 px-1 font-bold text-[12px]"
                                    style={{ color: settings.primaryColor }}
                                  >
                                    TOTAL NETO
                                  </td>
                                  <td
                                    className="py-2 px-1 font-black text-[12px]"
                                    style={{ color: settings.primaryColor }}
                                  >
                                    L. {formatMoney(total)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                      {dataRender.type.includes("COTIZA") && (
                        <div className="flex justify-end w-full mt-2">
                          <div className="w-full sm:w-5/12 text-center text-[8px] opacity-70 italic leading-tight">
                            Este documento es una cotización comercial, no
                            representa un comprobante fiscal.
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                {isLastPage && dataRender.type === "RECIBO" && (
                  <div className="mt-4 border border-gray-300 rounded-lg overflow-hidden text-[10px]">
                    <div
                      className="font-semibold text-center border-b border-gray-300 p-1.5 uppercase bg-gray-50"
                      style={{ color: settings.primaryColor }}
                    >
                      Resumen de Operación
                    </div>
                    <div className="flex flex-row">
                      <div className="w-1/3 border-r border-gray-300 p-3">
                        <div className="font-semibold mb-2 text-gray-500 flex items-center gap-1.5">
                          <div className="w-3 h-3 border border-gray-400 flex items-center justify-center rounded-sm">
                            {dataRender.paymentMethod === "EFECTIVO" && (
                              <span className="text-black font-bold text-[8px]">
                                ✓
                              </span>
                            )}
                          </div>{" "}
                          EFECTIVO
                        </div>
                        <div className="flex justify-between mb-1.5">
                          <span>Anticipo:</span>
                          <span className="font-mono">
                            {dataRender.paymentMethod === "EFECTIVO"
                              ? `L. ${formatMoney(dataRender.advance)}`
                              : ""}
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-gray-200 pt-1">
                          <span>Saldo Restante:</span>
                          <span className="font-mono font-bold">
                            {dataRender.paymentMethod === "EFECTIVO"
                              ? `L. ${formatMoney(balance)}`
                              : ""}
                          </span>
                        </div>
                      </div>
                      <div className="w-2/3 p-3">
                        <div className="font-semibold mb-2 text-gray-500 flex items-center gap-1.5">
                          <div className="w-3 h-3 border border-gray-400 flex items-center justify-center rounded-sm">
                            {dataRender.paymentMethod ===
                              "CHEQUE_TRANSFERENCIA" && (
                              <span className="text-black font-bold text-[8px]">
                                ✓
                              </span>
                            )}
                          </div>{" "}
                          CHEQUE / TRANSFERENCIA
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          <div className="flex justify-between border-b border-gray-100 pb-1">
                            <span className="text-gray-500">Banco:</span>
                            <span className="uppercase font-medium">
                              {dataRender.paymentMethod ===
                              "CHEQUE_TRANSFERENCIA"
                                ? dataRender.bank
                                : ""}
                            </span>
                          </div>
                          <div className="flex justify-between border-b border-gray-100 pb-1">
                            <span className="text-gray-500">Referencia:</span>
                            <span className="font-mono font-medium">
                              {dataRender.paymentMethod ===
                              "CHEQUE_TRANSFERENCIA"
                                ? dataRender.checkNumber
                                : ""}
                            </span>
                          </div>
                          <div className="flex justify-between border-b border-gray-100 pb-1 col-span-2">
                            <span className="text-gray-500">
                              Cuenta Depositada:
                            </span>
                            <span className="font-mono font-medium">
                              {dataRender.paymentMethod ===
                              "CHEQUE_TRANSFERENCIA"
                                ? dataRender.accountNumber
                                : ""}
                            </span>
                          </div>
                          <div className="flex justify-between pt-1">
                            <span className="text-gray-500">Anticipo:</span>
                            <span className="font-mono">
                              {dataRender.paymentMethod ===
                              "CHEQUE_TRANSFERENCIA"
                                ? `L. ${formatMoney(dataRender.advance)}`
                                : ""}
                            </span>
                          </div>
                          <div className="flex justify-between pt-1">
                            <span className="text-gray-500">
                              Saldo Restante:
                            </span>
                            <span className="font-mono font-bold">
                              {dataRender.paymentMethod ===
                              "CHEQUE_TRANSFERENCIA"
                                ? `L. ${formatMoney(balance)}`
                                : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isLastPage && dataRender.type === "COMPROBANTE_ENTREGA" && (
                  <div className="mt-6 text-[10px]">
                    <div className="mb-2 font-medium text-gray-600">
                      Certificamos la entrega satisfactoria de los bienes
                      detallados. Entregado a:
                    </div>
                    <div className="w-full">
                      <table className="w-full border-collapse border border-gray-300 text-left bg-transparent">
                        <thead className="bg-gray-50 border-b border-gray-300">
                          <tr>
                            <th
                              className="p-2 border-r border-gray-300 font-semibold"
                              style={{ color: settings.primaryColor }}
                            >
                              Nombre de Quien Recibe
                            </th>
                            <th
                              className="p-2 border-r border-gray-300 font-semibold"
                              style={{ color: settings.primaryColor }}
                            >
                              Doc. Identidad
                            </th>
                            <th
                              className="p-2 font-semibold"
                              style={{ color: settings.primaryColor }}
                            >
                              Fecha de Recepción
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="p-2 border-r border-gray-300 font-bold uppercase">
                              {dataRender.receiverName}
                            </td>
                            <td className="p-2 border-r border-gray-300 font-mono">
                              {dataRender.receiverDNI}
                            </td>
                            <td className="p-2 font-medium">
                              {formatLocalDate(dataRender.date)}{" "}
                              {dataRender.receiveTime}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* FIRMA Y SELLO DE GERENCIA - TAMAÑO FIJO PARA PDF */}
                {isLastPage && (
                  <div className="w-full flex justify-center items-end relative h-32 mt-12 mb-4 shrink-0">
                    {settings.signatureImg &&
                      dataRender.showSignature !== false && (
                        <img
                          src={settings.signatureImg}
                          alt="Firma"
                          className="absolute bottom-4 object-contain pointer-events-none"
                          style={{
                            width: "180px",
                            mixBlendMode: "multiply",
                            zIndex: 10,
                            left: "50%",
                            transform: "translateX(-50%)",
                          }}
                        />
                      )}
                    {settings.stampImg && dataRender.showStamp !== false && (
                      <img
                        src={settings.stampImg}
                        alt="Sello"
                        className="absolute bottom-[-10px] object-contain pointer-events-none"
                        style={{
                          width: "130px",
                          mixBlendMode: "multiply",
                          zIndex: 20,
                          left: "50%",
                          transform: "translateX(10%) rotate(-3deg)",
                        }}
                      />
                    )}
                    <div
                      className="border-t border-gray-400 text-center pt-2 text-[10px] z-0 font-semibold text-gray-600 relative mx-auto"
                      style={{ width: "250px" }}
                    >
                      AUTORIZADO POR LA GERENCIA
                    </div>
                  </div>
                )}

                {/* PIE DE PÁGINA */}
                <div className="mt-auto pt-4 flex flex-col justify-end shrink-0 w-full">
                  <div
                    className={`text-center w-full sm:w-4/5 mx-auto border-t border-gray-200 pt-3 ${
                      !isLastPage ? "pb-2" : ""
                    }`}
                  >
                    {isLastPage && (
                      <div
                        className="font-bold text-sm mb-1 tracking-tight"
                        style={{ color: settings.primaryColor }}
                      >
                        ¡GRACIAS POR SU PREFERENCIA!
                      </div>
                    )}
                    <div className="text-[9px] text-gray-500 whitespace-pre-wrap leading-relaxed px-2">
                      {settings.footerText}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d1116]">
        <Loader2 className="w-10 h-10 animate-spin text-white" />
      </div>
    );
  }

  if (!isUnlocked) {
    return (
      <div
        className={`min-h-screen transition-colors duration-300 bg-[#0d1116] text-gray-900 flex items-center justify-center p-4 font-sans`}
      >
        <div
          className={`${md3Card} p-6 sm:p-8 md:p-10 w-full max-w-md animate-in zoom-in-95 fade-in duration-300 shadow-xl`}
        >
          <div className="flex justify-center mb-6">
            {LOGIN_LOGO_URL ? (
              <img
                src={LOGIN_LOGO_URL}
                alt="Logo de Empresa"
                className="h-16 sm:h-20 object-contain mx-auto"
              />
            ) : (
              <div className="w-16 h-16 bg-[#252a50] rounded-2xl flex items-center justify-center text-white shadow-sm">
                <Lock className="w-8 h-8" />
              </div>
            )}
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-center text-[#252a50] mb-2 tracking-tight">
            Acceso Privado
          </h1>
          <p className="text-center text-sm text-gray-500 font-medium mb-8">
            Ingresa la clave de seguridad para continuar
          </p>

          <form onSubmit={handleUnlock} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="password"
                required
                placeholder="Clave de seguridad..."
                value={passInput}
                onChange={(e) => setPassInput(e.target.value)}
                className={`${mobbinInput} pl-11 text-black`}
              />
            </div>
            {authError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-xl text-xs font-medium text-center border border-red-100">
                {authError}
              </div>
            )}
            <button
              type="submit"
              className={`${mobbinBtnPrimary} w-full py-3 mt-2`}
            >
              Ingresar al Portal
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      id="keops-app-root"
      className="min-h-screen transition-colors duration-300 bg-[#0d1116] text-gray-900 flex flex-col font-sans selection:bg-gray-200 selection:text-black relative"
    >
      {/* NOTIFICACIÓN FLOTANTE */}
      {notification && (
        <div className="fixed top-4 right-4 bg-[#252a50] text-white px-4 py-3 rounded-xl shadow-2xl z-[9999] flex items-center gap-3 animate-in fade-in slide-in-from-top-4 print:hidden max-w-sm">
          <Sparkles className="w-5 h-5 text-emerald-400 shrink-0" />
          <span className="text-sm font-medium leading-tight">
            {notification}
          </span>
        </div>
      )}

      {/* BOTÓN FLOTANTE (FAB) PARA NUEVA COTIZACIÓN */}
      <div className="fixed bottom-20 md:bottom-8 right-4 md:right-8 z-[100] print:hidden">
        <button
          onClick={() => {
            setActiveTab("editor");
            handleNewDocument();
          }}
          className="bg-[#252a50] hover:bg-[#1b1f3b] text-white pl-4 pr-5 py-3.5 rounded-full shadow-xl flex items-center justify-center gap-2 transition-transform hover:scale-105 active:scale-95 font-semibold text-sm border-2 border-white/20"
        >
          <Plus className="w-5 h-5" />{" "}
          <span className="hidden sm:inline">Crear Nueva Cotización</span>
          <span className="sm:hidden">Nueva Cotización</span>
        </button>
      </div>

      {/* MODAL DE CONVERSIÓN */}
      {convertModal.isOpen && (
        <div className="fixed inset-0 bg-gray-900/40 z-[120] flex items-center justify-center p-4 backdrop-blur-sm print:hidden">
          <div
            className={`${md3Card} p-6 max-w-sm w-full animate-in fade-in zoom-in-95`}
          >
            <h2 className="text-lg font-bold text-[#252a50] mb-2">
              Convertir Documento
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              ¿Qué deseas crear a partir de esta cotización?
            </p>
            <div className="flex flex-col gap-3 mb-6">
              <button
                type="button"
                onClick={() =>
                  executeConversion(convertModal.docItem, "FACTURA")
                }
                className={mobbinBtnOutlined}
              >
                Factura Oficial
              </button>
              <button
                type="button"
                onClick={() =>
                  executeConversion(convertModal.docItem, "FACTURA_PROFORMA")
                }
                className={mobbinBtnOutlined}
              >
                Factura Proforma
              </button>
              <button
                type="button"
                onClick={() =>
                  executeConversion(convertModal.docItem, "COMPROBANTE_ENTREGA")
                }
                className={mobbinBtnOutlined}
              >
                Comprobante de Entrega
              </button>
              <button
                type="button"
                onClick={() =>
                  executeConversion(convertModal.docItem, "RECIBO")
                }
                className={mobbinBtnOutlined}
              >
                Recibo de Pago
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() =>
                  setConvertModal({ isOpen: false, docItem: null })
                }
                className={mobbinBtnTonal}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE PREVISUALIZACIÓN */}
      {previewDoc && (
        <div className="fixed inset-0 bg-gray-900/40 z-[110] flex items-center justify-center p-2 sm:p-4 md:p-8 backdrop-blur-sm print:static print:bg-transparent print:p-0 print:block">
          <div
            className={`${md3Card} w-full max-w-5xl h-full flex flex-col overflow-hidden animate-in fade-in zoom-in print:shadow-none print:border-none print:h-auto print:overflow-visible print:block print:rounded-none print:bg-transparent print:m-0`}
          >
            <div className="p-3 sm:p-4 md:px-6 bg-white flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-200 gap-3 print:hidden">
              <div>
                <h2 className="font-semibold text-base sm:text-lg text-[#252a50]">
                  Vista Previa
                </h2>
                <div className="text-[10px] sm:text-xs font-medium text-gray-500 mt-0.5">
                  {previewDoc.type.replace("_", " ")} #{previewDoc.docNumber}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center w-full sm:w-auto justify-end mt-2 sm:mt-0">
                <div className="hidden sm:flex bg-gray-100 rounded-xl items-center p-1 mr-2 border border-gray-200">
                  <button
                    type="button"
                    onClick={() => setZoomLevel((z) => Math.max(40, z - 10))}
                    className="p-1.5 hover:bg-white rounded-lg transition-colors"
                  >
                    <Minus className="w-3.5 h-3.5 text-gray-600" />
                  </button>
                  <span className="text-[10px] font-bold px-2 w-10 text-center text-[#252a50]">
                    {zoomLevel}%
                  </span>
                  <button
                    type="button"
                    onClick={() => setZoomLevel((z) => Math.min(200, z + 10))}
                    className="p-1.5 hover:bg-white rounded-lg transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5 text-gray-600" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const modalScroll = document.getElementById(
                      "preview-scroll-container"
                    );
                    const origScroll = modalScroll ? modalScroll.scrollTop : 0;
                    if (modalScroll) modalScroll.scrollTop = 0;
                    triggerNativePrint(previewDoc);
                    if (modalScroll)
                      setTimeout(
                        () => (modalScroll.scrollTop = origScroll),
                        500
                      );
                  }}
                  className={`${mobbinBtnOutlined} flex-1 sm:flex-none !py-2`}
                  title="Imprimir o Guardar como PDF"
                >
                  <Printer className="w-4 h-4" />{" "}
                  <span className="hidden sm:inline">Imprimir</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleShareDocument(previewDoc)}
                  disabled={isSharing}
                  className={`${mobbinBtnPrimary} flex-1 sm:flex-none !py-2`}
                >
                  {isSharing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Share2 className="w-4 h-4" />
                  )}{" "}
                  <span className="hidden sm:inline">Compartir</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleEditDocument(previewDoc);
                    setPreviewDoc(null);
                  }}
                  className={`${mobbinBtnTonal} flex-1 sm:flex-none !py-2`}
                >
                  <Edit className="w-4 h-4" />{" "}
                  <span className="hidden sm:inline">Editar</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  className={`${mobbinBtnOutlined} !py-2`}
                >
                  <X className="w-4 h-4" />{" "}
                  <span className="hidden sm:inline">Cerrar</span>
                </button>
              </div>
            </div>
            <div
              id="preview-scroll-container"
              className="overflow-auto p-2 sm:p-4 md:p-8 flex-1 bg-gray-100 relative print:p-0 print:bg-transparent print:overflow-visible print:block"
            >
              <div
                className="origin-top transition-transform duration-200 ease-out flex flex-col items-center print:static print:transform-none"
                style={{
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: "top center",
                }}
              >
                {renderDocumentPages(previewDoc)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMACIÓN */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-gray-900/40 z-[120] flex items-center justify-center p-4 backdrop-blur-sm print:hidden">
          <div
            className={`${md3Card} p-6 max-w-sm w-full animate-in fade-in zoom-in-95`}
          >
            <h2 className="text-lg font-bold text-[#252a50] mb-2">
              ¿Estás seguro?
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              {confirmDialog.message}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() =>
                  setConfirmDialog({
                    isOpen: false,
                    message: "",
                    onConfirm: null,
                  })
                }
                className={mobbinBtnOutlined}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDialog.onConfirm}
                className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2 text-sm font-medium transition-all active:scale-[0.98]"
              >
                Sí, Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VENTANA EMERGENTE DE VALIDACIÓN */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-gray-900/40 z-[100] flex items-center justify-center p-4 print:hidden backdrop-blur-sm transition-opacity">
          <div
            className={`${md3Card} p-6 max-w-md w-full animate-in fade-in zoom-in-95`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-lg font-bold text-[#252a50]">
                Revisión Recomendada
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Hemos detectado detalles en el documento antes de continuar:
            </p>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1.5 mb-6 max-h-40 overflow-y-auto p-3 bg-gray-50 rounded-xl border border-gray-200">
              {validationData.warnings.map((warning, index) => (
                <li key={index} className="font-medium">
                  {warning}
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowValidationModal(false)}
                className={mobbinBtnOutlined}
              >
                Corregir
              </button>
              <button
                type="button"
                onClick={() => executeAction(pendingAction)}
                disabled={validationData.hasCritical}
                className={
                  validationData.hasCritical
                    ? "bg-gray-200 text-gray-400 rounded-xl px-4 py-2 text-sm font-medium cursor-not-allowed"
                    : mobbinBtnPrimary
                }
                title={
                  validationData.hasCritical
                    ? "Debes solucionar los errores críticos para guardar"
                    : ""
                }
              >
                Ignorar y Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE TOP BAR */}
      <nav className="md:hidden print:hidden bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center sticky top-0 z-[60] shadow-sm">
        {PLATFORM_LOGO_URL ? (
          <img
            src={PLATFORM_LOGO_URL}
            alt="Logo"
            className="h-6 object-contain"
          />
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#252a50] rounded-md flex items-center justify-center text-white">
              <FileText className="w-3 h-3" />
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={handleLock}
          className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </nav>

      {/* MOBILE BOTTOM NAVIGATION */}
      <div className="md:hidden print:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around items-center px-2 py-1 z-[100] pb-safe shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <button
          type="button"
          onClick={() => setActiveTab("history")}
          className={`flex flex-col items-center p-2 rounded-xl transition-colors ${
            activeTab === "history" ? "text-[#252a50]" : "text-gray-400"
          }`}
        >
          <History className="w-5 h-5 mb-1" />
          <span className="text-[9px] font-semibold">Historial</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("editor")}
          className={`flex flex-col items-center p-2 rounded-xl transition-colors ${
            activeTab === "editor" ? "text-[#252a50]" : "text-gray-400"
          }`}
        >
          <FileText className="w-5 h-5 mb-1" />
          <span className="text-[9px] font-semibold">Editor</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("clients")}
          className={`flex flex-col items-center p-2 rounded-xl transition-colors ${
            activeTab === "clients" ? "text-[#252a50]" : "text-gray-400"
          }`}
        >
          <Users className="w-5 h-5 mb-1" />
          <span className="text-[9px] font-semibold">Clientes</span>
        </button>
        {/* PESTAÑA OCULTA TEMPORALMENTE 
        <button type="button" onClick={() => setActiveTab('bids')} className={`flex flex-col items-center p-2 rounded-xl transition-colors ${activeTab === 'bids' ? 'text-[#252a50]' : 'text-gray-400'}`}>
          <Landmark className="w-5 h-5 mb-1" /><span className="text-[9px] font-semibold">Licitaciones</span>
        </button>
        */}
        <button
          type="button"
          onClick={() => setActiveTab("finances")}
          className={`flex flex-col items-center p-2 rounded-xl transition-colors ${
            activeTab === "finances" ? "text-[#252a50]" : "text-gray-400"
          }`}
        >
          <PieChart className="w-5 h-5 mb-1" />
          <span className="text-[9px] font-semibold">Finanzas</span>
        </button>
      </div>

      {/* DESKTOP TOP APP BAR */}
      <nav className="hidden md:flex bg-white border-b border-gray-200 px-6 py-5 justify-between items-center print:hidden sticky top-0 z-[60] transition-colors shadow-sm">
        <div className="flex items-center gap-3">
          {PLATFORM_LOGO_URL ? (
            <img
              src={PLATFORM_LOGO_URL}
              alt="Logo"
              className="h-8 object-contain"
            />
          ) : (
            <>
              <div className="w-8 h-8 bg-[#252a50] rounded-lg flex items-center justify-center text-white">
                <FileText className="w-4 h-4" />
              </div>
              <span className="font-bold text-lg tracking-tight text-[#252a50]">
                PORTAL KEOPS
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar">
          <button
            type="button"
            onClick={() => setActiveTab("history")}
            className={`px-4 py-2.5 rounded-xl transition-colors text-sm font-semibold whitespace-nowrap flex items-center gap-2 ${
              activeTab === "history"
                ? "bg-gray-100 text-[#252a50]"
                : "text-gray-500 hover:text-[#252a50] hover:bg-gray-50"
            }`}
          >
            <History className="w-4 h-4" /> Historial
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("editor")}
            className={`px-4 py-2.5 rounded-xl transition-colors text-sm font-semibold whitespace-nowrap flex items-center gap-2 ${
              activeTab === "editor"
                ? "bg-gray-100 text-[#252a50]"
                : "text-gray-500 hover:text-[#252a50] hover:bg-gray-50"
            }`}
          >
            <FileText className="w-4 h-4" /> Editor
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("clients")}
            className={`px-4 py-2.5 rounded-xl transition-colors text-sm font-semibold whitespace-nowrap flex items-center gap-2 ${
              activeTab === "clients"
                ? "bg-gray-100 text-[#252a50]"
                : "text-gray-500 hover:text-[#252a50] hover:bg-gray-50"
            }`}
          >
            <Users className="w-4 h-4" /> Directorio
          </button>

          {/* PESTAÑA OCULTA TEMPORALMENTE 
          <button type="button" onClick={() => setActiveTab('bids')} className={`px-4 py-2.5 rounded-xl transition-colors text-sm font-semibold whitespace-nowrap flex items-center gap-2 ${activeTab === 'bids' ? 'bg-gray-100 text-[#252a50]' : 'text-gray-500 hover:text-[#252a50] hover:bg-gray-50'}`}><Landmark className="w-4 h-4"/> Licitaciones</button>
          */}

          <button
            type="button"
            onClick={() => setActiveTab("finances")}
            className={`px-4 py-2.5 rounded-xl transition-colors text-sm font-semibold whitespace-nowrap flex items-center gap-2 ${
              activeTab === "finances"
                ? "bg-gray-100 text-[#252a50]"
                : "text-gray-500 hover:text-[#252a50] hover:bg-gray-50"
            }`}
          >
            <PieChart className="w-4 h-4" /> Finanzas
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("settings")}
            className={`px-4 py-2.5 rounded-xl transition-colors text-sm font-semibold whitespace-nowrap flex items-center gap-2 ${
              activeTab === "settings"
                ? "bg-gray-100 text-[#252a50]"
                : "text-gray-500 hover:text-[#252a50] hover:bg-gray-50"
            }`}
          >
            <Settings className="w-4 h-4" /> Config
          </button>

          <div className="w-px h-6 bg-gray-200 mx-2"></div>

          <button
            type="button"
            onClick={handleLock}
            className="p-2.5 rounded-xl hover:bg-red-50 transition-colors text-gray-500 hover:text-red-600"
            title="Bloquear Portal"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </nav>

      {/* CONTENIDO PRINCIPAL */}
      <div
        className={`flex-1 p-2 sm:p-4 md:p-6 overflow-x-hidden overflow-y-auto pb-24 md:pb-6 ${
          previewDoc
            ? "print:hidden"
            : "print:p-0 print:overflow-visible print:block"
        }`}
      >
        {/* TABS: EDITOR & SETTINGS */}
        {["editor", "settings"].includes(activeTab) && (
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 h-auto lg:h-full print:h-auto print:block max-w-[1600px] mx-auto w-full">
            {/* PANEL IZQUIERDO */}
            <div
              className={`${md3Card} w-full lg:w-[480px] xl:w-[500px] p-4 sm:p-6 overflow-x-hidden overflow-y-visible lg:overflow-y-auto custom-scrollbar shrink-0 print:hidden flex flex-col gap-6`}
            >
              {activeTab === "settings" && (
                <>
                  <div className="flex items-center gap-2 border-b border-gray-200 pb-4">
                    <Settings className="w-5 h-5 text-gray-400" />
                    <h2 className="text-lg font-bold text-[#252a50] tracking-tight">
                      Preferencias del Sistema
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold mb-2 text-[#252a50] uppercase tracking-wider">
                        Formato de Hoja
                      </label>
                      <select
                        className={mobbinInput}
                        value={settings.format}
                        onChange={(e) =>
                          updateSetting("format", e.target.value)
                        }
                      >
                        <option value="A4">A4 (Estándar)</option>
                        <option value="Letter">Carta (Letter)</option>
                        <option value="Legal">Oficio (Legal)</option>
                        <option value="HalfLetter">Media Carta</option>
                        <option value="A5">A5</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-2 text-[#252a50] uppercase tracking-wider">
                        Tipografía
                      </label>
                      <select
                        className={mobbinInput}
                        value={settings.fontFamily}
                        onChange={(e) =>
                          updateSetting("fontFamily", e.target.value)
                        }
                      >
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="'Times New Roman', serif">
                          Times New Roman
                        </option>
                        <option value="'Courier New', monospace">
                          Courier
                        </option>
                        <option value="Inter, sans-serif">Inter</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-2 text-[#252a50] uppercase tracking-wider">
                        Escala del Texto
                      </label>
                      <select
                        className={mobbinInput}
                        value={settings.textScale || 1}
                        onChange={(e) =>
                          updateSetting("textScale", parseFloat(e.target.value))
                        }
                      >
                        <option value={0.8}>Muy Pequeño</option>
                        <option value={0.9}>Pequeño</option>
                        <option value={1}>Normal</option>
                        <option value={1.1}>Grande</option>
                        <option value={1.2}>Muy Grande</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className="w-10 h-10 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden"
                        style={{ backgroundColor: settings.bgColor }}
                      >
                        <input
                          type="color"
                          className="absolute inset-[-25%] w-[150%] h-[150%] cursor-pointer opacity-0"
                          value={settings.bgColor}
                          onChange={(e) =>
                            updateSetting("bgColor", e.target.value)
                          }
                          title="Cambiar color de fondo"
                        />
                      </div>
                      <label className="text-[10px] font-semibold text-gray-500">
                        Fondo
                      </label>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className="w-10 h-10 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden"
                        style={{ backgroundColor: settings.primaryColor }}
                      >
                        <input
                          type="color"
                          className="absolute inset-[-25%] w-[150%] h-[150%] cursor-pointer opacity-0"
                          value={settings.primaryColor}
                          onChange={(e) =>
                            updateSetting("primaryColor", e.target.value)
                          }
                          title="Cambiar color de acento"
                        />
                      </div>
                      <label className="text-[10px] font-semibold text-gray-500">
                        Acento
                      </label>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className="w-10 h-10 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden"
                        style={{ backgroundColor: settings.textColor }}
                      >
                        <input
                          type="color"
                          className="absolute inset-[-25%] w-[150%] h-[150%] cursor-pointer opacity-0"
                          value={settings.textColor}
                          onChange={(e) =>
                            updateSetting("textColor", e.target.value)
                          }
                          title="Cambiar color de texto"
                        />
                      </div>
                      <label className="text-[10px] font-semibold text-gray-500">
                        Texto
                      </label>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="25"
                        className="w-10 h-10 p-0 text-sm border border-gray-200 bg-gray-50 rounded-xl font-bold text-center outline-none focus:border-[#252a50]"
                        value={settings.itemsPerPage || 8}
                        onChange={(e) =>
                          updateSetting(
                            "itemsPerPage",
                            parseInt(e.target.value) || 8
                          )
                        }
                      />
                      <label className="text-[10px] font-semibold text-gray-500">
                        Ítems
                      </label>
                    </div>
                  </div>

                  <div className="space-y-4 pt-2">
                    <h3 className="font-bold text-xs text-[#252a50] uppercase tracking-wider">
                      Textos del Encabezado y Pie
                    </h3>
                    <input
                      type="text"
                      className={mobbinInput}
                      placeholder="Nombre de la Empresa Oficial"
                      value={settings.headerTitle}
                      onChange={(e) =>
                        updateSetting("headerTitle", e.target.value)
                      }
                    />
                    <textarea
                      className={`${mobbinInput} h-24 resize-none`}
                      placeholder="Párrafo de introducción o info de la empresa..."
                      value={settings.headerText}
                      onChange={(e) =>
                        updateSetting("headerText", e.target.value)
                      }
                    />
                    <textarea
                      className={`${mobbinInput} h-24 resize-none`}
                      placeholder="Texto de Pie de Página (Dirección, Teléfono)..."
                      value={settings.footerText}
                      onChange={(e) =>
                        updateSetting("footerText", e.target.value)
                      }
                    />
                  </div>

                  <div className="space-y-4 pt-4 border-t border-gray-200">
                    <h3 className="font-bold text-xs text-[#252a50] uppercase tracking-wider">
                      Datos Fiscales (SAR)
                    </h3>
                    <div>
                      <label className="block text-[10px] font-semibold mb-1 text-gray-600">
                        CAI por defecto (Factura Oficial)
                      </label>
                      <input
                        type="text"
                        className={`${mobbinInput} font-mono text-xs uppercase`}
                        placeholder="E29670-B1BEBB..."
                        value={settings.defaultCaiFactura || ""}
                        onChange={(e) =>
                          updateSetting("defaultCaiFactura", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold mb-1 text-gray-600">
                        CAI por defecto (Factura Proforma)
                      </label>
                      <input
                        type="text"
                        className={`${mobbinInput} font-mono text-xs uppercase`}
                        placeholder="28D927-519E00..."
                        value={settings.defaultCaiProforma || ""}
                        onChange={(e) =>
                          updateSetting("defaultCaiProforma", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t border-gray-200 mt-4">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-500" />
                      <h3 className="font-bold text-xs text-[#252a50] uppercase tracking-wider">
                        Integraciones (Webhooks)
                      </h3>
                    </div>
                    <p className="text-[10px] text-gray-500 leading-tight">
                      Configura las URLs de Make.com o Zapier para automatizar
                      el ciclo con el gobierno.
                    </p>

                    <div>
                      <label className="block text-[10px] font-semibold mb-1 text-gray-600">
                        Webhook: Lector de Honducompras (Python/Make)
                      </label>
                      <input
                        type="text"
                        className={`${mobbinInput} font-mono text-[10px]`}
                        placeholder="[https://hook.us1.make.com/](https://hook.us1.make.com/)..."
                        value={settings.honducomprasWebhook || ""}
                        onChange={(e) =>
                          updateSetting("honducomprasWebhook", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold mb-1 text-gray-600">
                        Webhook: Auto-Envío de Correos (EmailJS/Make)
                      </label>
                      <input
                        type="text"
                        className={`${mobbinInput} font-mono text-[10px]`}
                        placeholder="[https://hook.us1.make.com/](https://hook.us1.make.com/)..."
                        value={settings.emailWebhook || ""}
                        onChange={(e) =>
                          updateSetting("emailWebhook", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-gray-200 mt-4">
                    <h3 className="font-bold text-xs text-[#252a50] uppercase tracking-wider">
                      Recursos Gráficos (PNG/JPG)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="relative group">
                        <label
                          className={`flex flex-col items-center justify-center p-4 border border-dashed rounded-xl cursor-pointer transition-colors ${
                            settings.logoImg
                              ? "border-gray-400 bg-gray-50"
                              : "border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <ImageIcon
                            className={`w-5 h-5 mb-2 ${
                              settings.logoImg
                                ? "text-[#252a50]"
                                : "text-gray-400"
                            }`}
                          />
                          <span className="text-[11px] font-medium text-gray-600">
                            Logo Factura
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload(e, "logoImg")}
                          />
                        </label>
                        {settings.logoImg && (
                          <button
                            type="button"
                            onClick={() => updateSetting("logoImg", null)}
                            className="absolute -top-2 -right-2 bg-red-50 hover:bg-red-100 text-red-600 p-1.5 rounded-full border border-red-200"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="relative group">
                        <label
                          className={`flex flex-col items-center justify-center p-4 border border-dashed rounded-xl cursor-pointer transition-colors ${
                            settings.signatureImg
                              ? "border-gray-400 bg-gray-50"
                              : "border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <ImageIcon
                            className={`w-5 h-5 mb-2 ${
                              settings.signatureImg
                                ? "text-[#252a50]"
                                : "text-gray-400"
                            }`}
                          />
                          <span className="text-[11px] font-medium text-gray-600">
                            Firma Gerente
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) =>
                              handleImageUpload(e, "signatureImg")
                            }
                          />
                        </label>
                        {settings.signatureImg && (
                          <button
                            type="button"
                            onClick={() => updateSetting("signatureImg", null)}
                            className="absolute -top-2 -right-2 bg-red-50 hover:bg-red-100 text-red-600 p-1.5 rounded-full border border-red-200"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="relative group">
                        <label
                          className={`flex flex-col items-center justify-center p-4 border border-dashed rounded-xl cursor-pointer transition-colors ${
                            settings.stampImg
                              ? "border-gray-400 bg-gray-50"
                              : "border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <ImageIcon
                            className={`w-5 h-5 mb-2 ${
                              settings.stampImg
                                ? "text-[#252a50]"
                                : "text-gray-400"
                            }`}
                          />
                          <span className="text-[11px] font-medium text-gray-600">
                            Sello
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload(e, "stampImg")}
                          />
                        </label>
                        {settings.stampImg && (
                          <button
                            type="button"
                            onClick={() => updateSetting("stampImg", null)}
                            className="absolute -top-2 -right-2 bg-red-50 hover:bg-red-100 text-red-600 p-1.5 rounded-full border border-red-200"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="relative group">
                        <label
                          className={`flex flex-col items-center justify-center p-4 border border-dashed rounded-xl cursor-pointer transition-colors ${
                            settings.watermarkImg
                              ? "border-gray-400 bg-gray-50"
                              : "border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <ImageIcon
                            className={`w-5 h-5 mb-2 ${
                              settings.watermarkImg
                                ? "text-[#252a50]"
                                : "text-gray-400"
                            }`}
                          />
                          <span className="text-[11px] font-medium text-gray-600 text-center">
                            Marca Agua
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) =>
                              handleImageUpload(e, "watermarkImg")
                            }
                          />
                        </label>
                        {settings.watermarkImg && (
                          <button
                            type="button"
                            onClick={() => updateSetting("watermarkImg", null)}
                            className="absolute -top-2 -right-2 bg-red-50 hover:bg-red-100 text-red-600 p-1.5 rounded-full border border-red-200"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    className={`${mobbinBtnPrimary} w-full mt-4 py-3`}
                  >
                    <Save className="w-4 h-4" /> Guardar Preferencias
                  </button>
                </>
              )}

              {activeTab === "editor" && (
                <>
                  <div className="flex justify-between items-center border-b border-gray-200 pb-3">
                    <h2 className="text-lg font-bold flex items-center gap-2 text-[#252a50] tracking-tight">
                      Editor Documental
                    </h2>
                  </div>

                  <div className="flex flex-col gap-3 mt-4 mb-3 w-full">
                    {/* Fila 1: Imprimir, Nuevo */}
                    <div className="flex gap-2 w-full">
                      <button
                        type="button"
                        onClick={() => triggerNativePrint(docData)}
                        className={`${mobbinBtnOutlined} flex-[2] !py-3 sm:!py-2`}
                        title="Imprimir"
                      >
                        <Printer className="w-5 h-5 sm:w-4 sm:h-4 mr-1.5 inline" />{" "}
                        <span className="font-bold text-sm">Imprimir</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleNewDocument}
                        className={`${mobbinBtnTonal} flex-[1] !py-3 sm:!py-2`}
                      >
                        <Plus className="w-5 h-5 sm:w-4 sm:h-4 mr-1.5 inline" />{" "}
                        <span className="font-bold text-sm">Nuevo</span>
                      </button>
                    </div>
                    {/* Fila 2: Convertir/Copiar, Actualizar/Guardar */}
                    <div className="flex gap-2 w-full">
                      {docData.id && (
                        <button
                          type="button"
                          onClick={handlePrepareNew}
                          className={`${mobbinBtnTonal} flex-1 !py-3 sm:!py-2`}
                          title="Crear documento nuevo a partir de este"
                        >
                          <CopyIcon className="w-5 h-5 sm:w-4 sm:h-4 mr-1.5 inline" />{" "}
                          <span className="font-bold text-sm truncate">
                            Convertir / Copiar
                          </span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => requestAction("save")}
                        className={`${mobbinBtnPrimary} flex-1 !py-3 sm:!py-2`}
                      >
                        <Save className="w-5 h-5 sm:w-4 sm:h-4 mr-1.5 inline" />{" "}
                        <span className="font-bold text-sm">
                          {docData.id ? "Actualizar" : "Guardar"}
                        </span>
                      </button>
                    </div>
                  </div>

                  {emailDraft && (
                    <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl relative animate-in fade-in mt-2">
                      <button
                        type="button"
                        onClick={() => setEmailDraft("")}
                        className="absolute top-2 right-2 text-gray-400 hover:text-[#252a50] p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Borrador Generado:
                      </div>
                      <div className="whitespace-pre-wrap bg-white p-3 rounded-lg border border-gray-200 text-xs text-gray-700 leading-relaxed font-mono">
                        {emailDraft}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => changeEmailTone("Urgente")}
                          className="text-[10px] text-gray-500 hover:text-[#252a50] border border-gray-200 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Tono Urgente
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(emailDraft, () =>
                              showNotification("Copiado al portapapeles")
                            )
                          }
                          className="text-[10px] bg-[#252a50] text-white px-4 py-1.5 rounded-lg font-medium hover:opacity-80 flex items-center gap-1.5 transition-colors"
                        >
                          <Copy className="w-3 h-3" /> Copiar Todo
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 w-full">
                    <div className="min-w-0">
                      <label className="block text-[11px] font-semibold mb-1.5 text-[#252a50] uppercase tracking-wide truncate">
                        Tipo de Doc.
                      </label>
                      <select
                        className={`${mobbinInput} px-2 text-xs sm:text-sm min-w-0`}
                        value={docData.type}
                        onChange={(e) => handleTypeChange(e.target.value)}
                      >
                        <option value="COTIZACIÓN">Cotización</option>
                        <option value="FACTURA">Factura</option>
                        <option value="FACTURA_PROFORMA">Proforma</option>
                        <option value="COMPROBANTE_ENTREGA">Entrega</option>
                        <option value="RECIBO">Recibo</option>
                      </select>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-[11px] font-semibold mb-1.5 text-[#252a50] uppercase tracking-wide truncate">
                        Fecha
                      </label>
                      <input
                        type="date"
                        className={`${mobbinInput} px-2 text-xs sm:text-sm min-w-0 appearance-none`}
                        value={docData.date}
                        onChange={(e) => updateDoc("date", e.target.value)}
                      />
                    </div>
                    <div className="min-w-0 sm:col-span-1 col-span-2">
                      <label className="block text-[11px] font-semibold mb-1.5 text-[#252a50] uppercase tracking-wide truncate">
                        Ítems p/ pág.
                      </label>
                      <input
                        type="number"
                        placeholder={`Auto (${settings.itemsPerPage || 8})`}
                        className={`${mobbinInput} px-2 text-xs sm:text-sm min-w-0`}
                        value={docData.customItemsPerPage || ""}
                        onChange={(e) =>
                          updateDoc("customItemsPerPage", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 w-full">
                    <div className="min-w-0 w-full">
                      <label className="flex justify-between items-center text-[11px] font-semibold mb-1.5 text-[#252a50] uppercase tracking-wide truncate">
                        Núm. de Documento{" "}
                        {!docData.id && (
                          <button
                            type="button"
                            onClick={() =>
                              updateDoc(
                                "docNumber",
                                getNextDocNumber(docData.type, historyDocs)
                              )
                            }
                            className="text-[#252a50] capitalize font-bold hover:underline"
                          >
                            Autogenerar
                          </button>
                        )}
                      </label>
                      <input
                        type="text"
                        className={`${mobbinInput} font-mono font-medium ${
                          docData.id ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        value={docData.docNumber}
                        onChange={(e) => updateDoc("docNumber", e.target.value)}
                        readOnly={!!docData.id}
                      />
                    </div>
                    {["FACTURA", "FACTURA_PROFORMA"].includes(docData.type) && (
                      <div className="min-w-0 w-full">
                        <label className="block text-[11px] font-semibold mb-1.5 text-[#252a50] uppercase tracking-wide truncate">
                          CAI Fiscal
                        </label>
                        <input
                          type="text"
                          className={`${mobbinInput} uppercase font-mono text-xs`}
                          value={docData.cai}
                          onChange={(e) => updateDoc("cai", e.target.value)}
                          placeholder="000000-000000..."
                        />
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 pt-5">
                    <h3 className="font-semibold text-sm mb-3 text-[#252a50]">
                      Datos del Cliente
                    </h3>
                    <div className="space-y-3">
                      <datalist id="clients-list">
                        {mergedClientsDB.map((c) => (
                          <option key={c.name} value={c.name} />
                        ))}
                      </datalist>
                      <input
                        type="text"
                        list="clients-list"
                        placeholder="Razón Social / Cliente"
                        className={mobbinInput}
                        value={docData.clientName}
                        onChange={handleClientChange}
                      />

                      {docData.type !== "RECIBO" && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                            <div className="min-w-0 w-full">
                              <input
                                type="text"
                                placeholder="RTN"
                                className={mobbinInput}
                                value={docData.clientRTN}
                                onChange={(e) =>
                                  updateDoc("clientRTN", e.target.value)
                                }
                              />
                            </div>
                            <div className="min-w-0 w-full">
                              <input
                                type="text"
                                placeholder="Atención a"
                                className={mobbinInput}
                                value={docData.clientCompany}
                                onChange={(e) =>
                                  updateDoc("clientCompany", e.target.value)
                                }
                              />
                            </div>
                          </div>
                          <div className="min-w-0 w-full">
                            <input
                              type="text"
                              placeholder="Dirección del Cliente"
                              className={mobbinInput}
                              value={docData.clientAddress || ""}
                              onChange={(e) =>
                                updateDoc("clientAddress", e.target.value)
                              }
                            />
                          </div>
                        </>
                      )}

                      <div className="mt-2 bg-gray-50 p-3 rounded-xl border border-gray-200 flex flex-col gap-3">
                        <label className="flex items-center gap-3 text-sm font-medium cursor-pointer text-gray-900">
                          <div
                            className={`w-4 h-4 rounded-sm flex items-center justify-center transition-colors border ${
                              docData.applyISV !== false
                                ? "bg-[#252a50] border-[#252a50]"
                                : "border-gray-300"
                            }`}
                          >
                            {docData.applyISV !== false && (
                              <CheckCircle className="w-3 h-3 text-white pointer-events-none" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={docData.applyISV !== false}
                            onChange={(e) =>
                              updateDoc("applyISV", e.target.checked)
                            }
                            className="hidden"
                          />
                          Calcular I.S.V. (15%) al final
                        </label>
                        <label className="flex items-center gap-3 text-sm font-medium cursor-pointer text-gray-900">
                          <div
                            className={`w-4 h-4 rounded-sm flex items-center justify-center transition-colors border ${
                              docData.isGovProcess
                                ? "bg-[#252a50] border-[#252a50]"
                                : "border-gray-300"
                            }`}
                          >
                            {docData.isGovProcess && (
                              <CheckCircle className="w-3 h-3 text-white pointer-events-none" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={docData.isGovProcess || false}
                            onChange={(e) =>
                              updateDoc("isGovProcess", e.target.checked)
                            }
                            className="hidden"
                          />
                          Proceso Gubernamental
                        </label>
                        {docData.isGovProcess && (
                          <input
                            type="text"
                            placeholder="Número de O.C. o Contrato..."
                            className={`${mobbinInput} bg-white`}
                            value={docData.govProcessCode || ""}
                            onChange={(e) =>
                              updateDoc("govProcessCode", e.target.value)
                            }
                          />
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Vendedor / Autor"
                        className={mobbinInput}
                        value={docData.author}
                        onChange={(e) => updateDoc("author", e.target.value)}
                      />
                    </div>
                  </div>

                  {docData.type !== "RECIBO" &&
                    docData.type !== "COMPROBANTE_ENTREGA" && (
                      <div className="border-t border-gray-200 pt-5">
                        <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
                          <h3 className="font-semibold text-sm text-[#252a50]">
                            Ítems a Cobrar
                          </h3>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={enhanceAllDescriptions}
                              disabled={
                                isEnhancingAll ||
                                docData.items.every(
                                  (i) => !i.description.trim()
                                )
                              }
                              className={
                                mobbinBtnTonal + " !py-1.5 !px-2.5 !text-[10px]"
                              }
                            >
                              {isEnhancingAll ? (
                                <Loader2 className="w-3 h-3 animate-spin inline" />
                              ) : (
                                <Sparkles className="w-3 h-3 inline" />
                              )}{" "}
                              <span className="hidden sm:inline">
                                Mejorar Todos
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowImportBox(!showImportBox)}
                              className={
                                mobbinBtnTonal + " !py-1.5 !px-2.5 !text-[10px]"
                              }
                            >
                              Importar
                            </button>
                            <button
                              type="button"
                              onClick={addItem}
                              className={
                                mobbinBtnOutlined +
                                " !py-1.5 !px-2.5 !text-[10px]"
                              }
                            >
                              + Fila
                            </button>
                          </div>
                        </div>

                        {renderAutoFillBox()}

                        <div className="space-y-3">
                          {docData.items.map((item, index) => (
                            <div
                              key={item.id}
                              draggable
                              onDragStart={() => setDraggedIndex(index)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => {
                                e.preventDefault();
                                if (
                                  draggedIndex === null ||
                                  draggedIndex === index
                                )
                                  return;
                                const newItems = [...docData.items];
                                const draggedItem = newItems[draggedIndex];
                                newItems.splice(draggedIndex, 1);
                                newItems.splice(index, 0, draggedItem);
                                updateDoc("items", newItems);
                                setDraggedIndex(null);
                              }}
                              className={`bg-white p-3 rounded-xl border relative flex flex-col group transition-all duration-300 ${
                                draggedIndex === index
                                  ? "opacity-40 border-dashed border-gray-400"
                                  : "border-gray-200 hover:border-gray-400"
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() => removeItem(item.id)}
                                className="absolute top-2 right-2 text-gray-300 hover:text-red-500 bg-gray-50 rounded-lg p-1 sm:opacity-0 group-hover:opacity-100 transition-all z-10"
                              >
                                <Trash2 className="w-3.5 h-3.5 pointer-events-none" />
                              </button>
                              <div
                                className="absolute left-0 top-0 bottom-0 w-6 flex flex-col items-center justify-center cursor-grab active:cursor-grabbing text-gray-300 hover:text-[#252a50] transition-colors"
                                title="Arrastrar para reordenar"
                              >
                                <div className="flex flex-col gap-1">
                                  <div className="w-1 h-1 bg-current rounded-full"></div>
                                  <div className="w-1 h-1 bg-current rounded-full"></div>
                                  <div className="w-1 h-1 bg-current rounded-full"></div>
                                </div>
                              </div>
                              <div className="pl-6 w-full">
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2 pr-6">
                                  <input
                                    type="number"
                                    placeholder="Cant."
                                    className={`${mobbinInput} col-span-1`}
                                    value={
                                      item.quantity === 0 ? "" : item.quantity
                                    }
                                    onChange={(e) =>
                                      updateItem(
                                        item.id,
                                        "quantity",
                                        e.target.value
                                      )
                                    }
                                  />
                                  <div className="col-span-2 sm:col-span-3 relative">
                                    <span className="absolute left-3 top-2.5 text-gray-400 font-bold text-sm pointer-events-none">
                                      L.
                                    </span>
                                    <input
                                      type="number"
                                      placeholder="Precio"
                                      className={`${mobbinInput} pl-8 w-full font-mono`}
                                      value={
                                        item.unitPrice === 0
                                          ? ""
                                          : item.unitPrice
                                      }
                                      onChange={(e) =>
                                        updateItem(
                                          item.id,
                                          "unitPrice",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="relative">
                                  <textarea
                                    placeholder="Descripción detallada del ítem..."
                                    className={`${mobbinInput} h-24 resize-none pr-10`}
                                    value={item.description}
                                    onChange={(e) =>
                                      handleItemDescChange(
                                        item.id,
                                        e.target.value
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      enhanceDescription(
                                        item.id,
                                        item.description
                                      )
                                    }
                                    disabled={
                                      loadingItemIds.has(item.id) ||
                                      !item.description.trim()
                                    }
                                    className="absolute bottom-2 right-2 bg-gray-100 hover:bg-gray-200 text-[#252a50] p-1.5 rounded-lg transition-all disabled:opacity-50"
                                    title="Mejorar con IA"
                                  >
                                    {loadingItemIds.has(item.id) ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Sparkles className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2 justify-between items-center">
                          <label className="text-[11px] font-semibold text-[#252a50] uppercase tracking-wide">
                            Notas y Condiciones
                          </label>
                          <button
                            type="button"
                            onClick={suggestNotes}
                            disabled={isGeneratingNotes}
                            className="text-[10px] bg-gray-100 hover:bg-gray-200 text-[#252a50] px-2 py-1 rounded-md font-medium transition-colors"
                          >
                            Sugerir texto
                          </button>
                          <button
                            type="button"
                            onClick={generateEmailDraft}
                            disabled={isGeneratingEmail}
                            className="text-[10px] bg-gray-100 hover:bg-gray-200 text-[#252a50] px-2 py-1 rounded-md font-medium transition-colors"
                          >
                            Borrador de Email
                          </button>
                        </div>
                        <div className="mt-2">
                          <textarea
                            className={`${mobbinInput} h-24 resize-none`}
                            placeholder="Condiciones de pago, validez..."
                            value={docData.notes}
                            onChange={(e) => updateDoc("notes", e.target.value)}
                          />
                        </div>

                        {["FACTURA", "FACTURA_PROFORMA"].includes(
                          docData.type
                        ) && (
                          <div className="mt-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
                            <h3 className="font-semibold text-sm mb-4 text-[#252a50]">
                              Impuestos e Importes Fijos
                            </h3>
                            <div className="space-y-3 mb-4">
                              <input
                                type="text"
                                placeholder="Orden de Compra Exenta"
                                className={mobbinInput}
                                value={docData.exemptOrder}
                                onChange={(e) =>
                                  updateDoc("exemptOrder", e.target.value)
                                }
                              />
                              <input
                                type="text"
                                placeholder="Constancia Registro Exonerado"
                                className={mobbinInput}
                                value={docData.exemptRegistry}
                                onChange={(e) =>
                                  updateDoc("exemptRegistry", e.target.value)
                                }
                              />
                              <input
                                type="text"
                                placeholder="Registro SAG"
                                className={mobbinInput}
                                value={docData.sagRegistry}
                                onChange={(e) =>
                                  updateDoc("sagRegistry", e.target.value)
                                }
                              />
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 w-full">
                              <div className="min-w-0 w-full">
                                <label className="block text-[10px] font-medium mb-1 text-gray-500 truncate">
                                  Exonerado
                                </label>
                                <input
                                  type="number"
                                  className={mobbinInput}
                                  value={
                                    docData.importeExonerado === 0
                                      ? ""
                                      : docData.importeExonerado
                                  }
                                  onChange={(e) =>
                                    updateDoc(
                                      "importeExonerado",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="min-w-0 w-full">
                                <label className="block text-[10px] font-medium mb-1 text-gray-500 truncate">
                                  Exento
                                </label>
                                <input
                                  type="number"
                                  className={mobbinInput}
                                  value={
                                    docData.importeExento === 0
                                      ? ""
                                      : docData.importeExento
                                  }
                                  onChange={(e) =>
                                    updateDoc("importeExento", e.target.value)
                                  }
                                />
                              </div>
                              <div className="min-w-0 w-full">
                                <label className="block text-[10px] font-medium mb-1 text-gray-500 truncate">
                                  Gravado 18%
                                </label>
                                <input
                                  type="number"
                                  className={mobbinInput}
                                  value={
                                    docData.importeGravado18 === 0
                                      ? ""
                                      : docData.importeGravado18
                                  }
                                  onChange={(e) =>
                                    updateDoc(
                                      "importeGravado18",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>

                              <div className="min-w-0 w-full">
                                <label className="flex justify-between items-center text-[10px] font-medium mb-1 text-gray-500 truncate">
                                  Gravado 15%{" "}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateDoc("importeGravado15", "")
                                    }
                                    className="text-[#252a50] font-bold hover:underline"
                                  >
                                    Auto
                                  </button>
                                </label>
                                <input
                                  type="number"
                                  placeholder={`Auto: ${currentTotals.impGravado15_calc.toFixed(
                                    2
                                  )}`}
                                  className={mobbinInput}
                                  value={
                                    docData.importeGravado15 !== undefined
                                      ? docData.importeGravado15
                                      : ""
                                  }
                                  onChange={(e) =>
                                    updateDoc(
                                      "importeGravado15",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="min-w-0 w-full">
                                <label className="flex justify-between items-center text-[10px] font-medium mb-1 text-gray-500 truncate">
                                  I.S.V. 15%{" "}
                                  <button
                                    type="button"
                                    onClick={() => updateDoc("isv15", "")}
                                    className="text-[#252a50] font-bold hover:underline"
                                  >
                                    Auto
                                  </button>
                                </label>
                                <input
                                  type="number"
                                  placeholder={`Auto: ${currentTotals.isv15_calc.toFixed(
                                    2
                                  )}`}
                                  className={mobbinInput}
                                  value={
                                    docData.isv15 !== undefined
                                      ? docData.isv15
                                      : ""
                                  }
                                  onChange={(e) =>
                                    updateDoc("isv15", e.target.value)
                                  }
                                />
                              </div>
                              <div className="min-w-0 w-full">
                                <label className="flex justify-between items-center text-[10px] font-medium mb-1 text-gray-500 truncate">
                                  I.S.V. 18%{" "}
                                  <button
                                    type="button"
                                    onClick={() => updateDoc("isv18", "")}
                                    className="text-[#252a50] font-bold hover:underline"
                                  >
                                    Auto
                                  </button>
                                </label>
                                <input
                                  type="number"
                                  placeholder={`Auto: ${currentTotals.isv18_calc.toFixed(
                                    2
                                  )}`}
                                  className={mobbinInput}
                                  value={
                                    docData.isv18 !== undefined
                                      ? docData.isv18
                                      : ""
                                  }
                                  onChange={(e) =>
                                    updateDoc("isv18", e.target.value)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  {docData.type === "COMPROBANTE_ENTREGA" && (
                    <div className="border-t border-gray-200 pt-5 mt-2">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-semibold text-sm text-[#252a50]">
                          Materiales a Entregar
                        </h3>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={addItem}
                            className={
                              mobbinBtnOutlined + " !py-1 !px-2 !text-[10px]"
                            }
                          >
                            + Fila
                          </button>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {docData.items.map((item, index) => (
                          <div
                            key={item.id}
                            className="bg-white p-3 rounded-xl border border-gray-200 relative flex items-start gap-2 group"
                          >
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              className="text-gray-300 hover:text-red-500 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <input
                              type="number"
                              placeholder="Cant."
                              className={`${mobbinInput} w-20`}
                              value={item.quantity === 0 ? "" : item.quantity}
                              onChange={(e) =>
                                updateItem(item.id, "quantity", e.target.value)
                              }
                            />
                            <textarea
                              placeholder="Descripción..."
                              className={`${mobbinInput} flex-1 h-10 resize-none`}
                              value={item.description}
                              onChange={(e) =>
                                handleItemDescChange(item.id, e.target.value)
                              }
                            />
                          </div>
                        ))}
                      </div>
                      <div className="mt-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <h3 className="font-semibold text-sm mb-3 text-[#252a50]">
                          Recepción
                        </h3>
                        <div className="space-y-3">
                          <input
                            type="text"
                            placeholder="Nombre de quien recibe"
                            className={mobbinInput}
                            value={docData.receiverName}
                            onChange={(e) =>
                              updateDoc("receiverName", e.target.value)
                            }
                          />
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input
                              type="text"
                              placeholder="DNI Oficial"
                              className={mobbinInput}
                              value={docData.receiverDNI}
                              onChange={(e) =>
                                updateDoc("receiverDNI", e.target.value)
                              }
                            />
                            <input
                              type="time"
                              className={mobbinInput}
                              value={docData.receiveTime}
                              onChange={(e) =>
                                updateDoc("receiveTime", e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {docData.type === "RECIBO" && (
                    <div className="border-t border-gray-200 pt-5 mt-2">
                      <h3 className="font-semibold text-sm mb-4 text-[#252a50]">
                        Detalles del Pago
                      </h3>
                      <div className="space-y-4">
                        <input
                          type="text"
                          placeholder="Concepto..."
                          className={mobbinInput}
                          value={docData.receiptConcept}
                          onChange={(e) =>
                            updateDoc("receiptConcept", e.target.value)
                          }
                        />
                        <textarea
                          className={`${mobbinInput} h-16 resize-none`}
                          placeholder="Observaciones"
                          value={docData.notes}
                          onChange={(e) => updateDoc("notes", e.target.value)}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-semibold mb-1.5 text-[#252a50] uppercase">
                              Total a Cobrar
                            </label>
                            <input
                              type="number"
                              className={`${mobbinInput} font-bold`}
                              value={
                                docData.receiptTotal !== undefined
                                  ? docData.receiptTotal
                                  : ""
                              }
                              onChange={(e) =>
                                updateDoc("receiptTotal", e.target.value)
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold mb-1.5 text-[#252a50] uppercase">
                              Anticipo
                            </label>
                            <input
                              type="number"
                              className={mobbinInput}
                              value={
                                docData.advance === 0 ? "" : docData.advance
                              }
                              onChange={(e) =>
                                updateDoc("advance", e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div className="mt-2 p-4 bg-gray-50 rounded-xl border border-gray-200">
                          <label className="block text-sm font-semibold mb-3 text-[#252a50]">
                            Método
                          </label>
                          <div className="flex flex-col sm:flex-row gap-4 mb-4">
                            <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-800">
                              <input
                                type="radio"
                                checked={docData.paymentMethod === "EFECTIVO"}
                                onChange={() =>
                                  updateDoc("paymentMethod", "EFECTIVO")
                                }
                                className="w-4 h-4 text-[#252a50] bg-white border-gray-300 focus:ring-[#252a50]"
                              />{" "}
                              Efectivo
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-800">
                              <input
                                type="radio"
                                checked={
                                  docData.paymentMethod ===
                                  "CHEQUE_TRANSFERENCIA"
                                }
                                onChange={() =>
                                  updateDoc(
                                    "paymentMethod",
                                    "CHEQUE_TRANSFERENCIA"
                                  )
                                }
                                className="w-4 h-4 text-[#252a50] bg-white border-gray-300 focus:ring-[#252a50]"
                              />{" "}
                              Cheque / Transf.
                            </label>
                          </div>
                          {docData.paymentMethod === "CHEQUE_TRANSFERENCIA" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-gray-200">
                              <select
                                className={mobbinInput}
                                value={docData.bank}
                                onChange={(e) =>
                                  updateDoc("bank", e.target.value)
                                }
                              >
                                <option value="">Banco...</option>
                                {bancosHonduras.map((b) => (
                                  <option key={b} value={b}>
                                    {b}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="text"
                                placeholder="Cuenta"
                                className={`${mobbinInput} font-mono`}
                                value={docData.accountNumber}
                                onChange={(e) =>
                                  updateDoc("accountNumber", e.target.value)
                                }
                              />
                              <input
                                type="text"
                                placeholder="Ref/Cheque"
                                className={`${mobbinInput} sm:col-span-2 font-mono`}
                                value={docData.checkNumber || ""}
                                onChange={(e) =>
                                  updateDoc("checkNumber", e.target.value)
                                }
                              />
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                          <input
                            type="text"
                            placeholder="O.T. No."
                            className={`${mobbinInput} font-mono`}
                            value={docData.otNumber || ""}
                            onChange={(e) =>
                              updateDoc("otNumber", e.target.value)
                            }
                          />
                          <input
                            type="text"
                            placeholder="C.E. No."
                            className={`${mobbinInput} font-mono`}
                            value={docData.ceNumber || ""}
                            onChange={(e) =>
                              updateDoc("ceNumber", e.target.value)
                            }
                          />
                          <input
                            type="text"
                            placeholder="Recibido Por"
                            className={mobbinInput}
                            value={docData.receiverName}
                            onChange={(e) =>
                              updateDoc("receiverName", e.target.value)
                            }
                          />
                          <input
                            type="text"
                            placeholder="DNI"
                            className={`${mobbinInput} font-mono`}
                            value={docData.receiverDNI}
                            onChange={(e) =>
                              updateDoc("receiverDNI", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* VISIBILIDAD DE FIRMA Y SELLO */}
                  <div className="border-t border-gray-200 pt-5 mt-4">
                    <h3 className="font-semibold text-sm mb-3 text-[#252a50] flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-gray-500" />{" "}
                      Visibilidad en el Papel
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <label className="flex items-center gap-3 text-sm font-medium cursor-pointer text-gray-800 bg-gray-50 px-4 py-2.5 rounded-xl border border-gray-200 hover:border-gray-400 transition-colors flex-1">
                        <div
                          className={`w-4 h-4 rounded-sm flex items-center justify-center transition-colors border ${
                            docData.showSignature !== false
                              ? "bg-[#252a50] border-[#252a50]"
                              : "border-gray-300"
                          }`}
                        >
                          {docData.showSignature !== false && (
                            <CheckCircle className="w-3 h-3 text-white pointer-events-none" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={docData.showSignature !== false}
                          onChange={(e) =>
                            updateDoc("showSignature", e.target.checked)
                          }
                          className="hidden"
                        />
                        Mostrar Firma
                      </label>
                      <label className="flex items-center gap-3 text-sm font-medium cursor-pointer text-gray-800 bg-gray-50 px-4 py-2.5 rounded-xl border border-gray-200 hover:border-gray-400 transition-colors flex-1">
                        <div
                          className={`w-4 h-4 rounded-sm flex items-center justify-center transition-colors border ${
                            docData.showStamp !== false
                              ? "bg-[#252a50] border-[#252a50]"
                              : "border-gray-300"
                          }`}
                        >
                          {docData.showStamp !== false && (
                            <CheckCircle className="w-3 h-3 text-white pointer-events-none" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={docData.showStamp !== false}
                          onChange={(e) =>
                            updateDoc("showStamp", e.target.checked)
                          }
                          className="hidden"
                        />
                        Mostrar Sello
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* PANEL DERECHO (VISTA PREVIA CON ZOOM) */}
            <div className="w-full flex-1 bg-transparent p-0 sm:p-2 md:p-6 lg:rounded-2xl overflow-auto print:p-0 print:m-0 print:bg-white print:block print:w-full transition-colors relative custom-scrollbar flex flex-col items-center">
              {/* ZOOM CONTROLS (Solo visible en pantalla) */}
              <div className="sticky top-4 z-[40] mb-6 print:hidden flex justify-center w-full pointer-events-none">
                <div className="bg-white/90 backdrop-blur-md border border-gray-200 rounded-xl shadow-lg flex items-center p-1 pointer-events-auto">
                  <button
                    type="button"
                    onClick={() => setZoomLevel((z) => Math.max(40, z - 10))}
                    className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-bold px-2 w-14 text-center text-[#252a50]">
                    {zoomLevel}%
                  </span>
                  <button
                    type="button"
                    onClick={() => setZoomLevel((z) => Math.min(200, z + 10))}
                    className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* CONTENEDOR ESCALABLE */}
              <div
                className="origin-top transition-transform duration-200 ease-out flex flex-col items-center min-w-max pb-12 print:pb-0"
                style={{
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: "top center",
                }}
              >
                {renderDocumentPages()}
              </div>
            </div>
          </div>
        )}

        {/* TAB: CLIENTES (DIRECTORIO) */}
        {activeTab === "clients" && (
          <div className="max-w-6xl mx-auto animate-in fade-in">
            <div className={`${md3Card} p-4 sm:p-6 md:p-8`}>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-[#252a50]" />
                  <h2 className="text-xl font-bold text-[#252a50]">
                    Directorio
                  </h2>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="w-full sm:w-72 relative">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre, RTN, correo..."
                      className={`${mobbinInput} pl-10`}
                      value={clientSearchTerm}
                      onChange={(e) => setClientSearchTerm(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setClientSortOrder((o) => (o === "asc" ? "desc" : "asc"))
                    }
                    className={`${mobbinBtnOutlined} !px-3`}
                    title="Ordenar por nombre"
                  >
                    {clientSortOrder === "asc" ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronUp className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 p-4 sm:p-6 rounded-2xl border border-gray-200 mb-8">
                <h3 className="text-sm font-semibold text-[#252a50] mb-4">
                  Registrar Nuevo Cliente
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <input
                    placeholder="Razón Social *"
                    value={newClient.name}
                    onChange={(e) =>
                      setNewClient({ ...newClient, name: e.target.value })
                    }
                    className={mobbinInput}
                  />
                  <input
                    placeholder="RTN Oficial"
                    value={newClient.rtn}
                    onChange={(e) =>
                      setNewClient({ ...newClient, rtn: e.target.value })
                    }
                    className={mobbinInput}
                  />
                  <input
                    placeholder="Atención a (Contacto)"
                    value={newClient.contact}
                    onChange={(e) =>
                      setNewClient({ ...newClient, contact: e.target.value })
                    }
                    className={mobbinInput}
                  />
                  <input
                    placeholder="Teléfono"
                    value={newClient.phone}
                    onChange={(e) =>
                      setNewClient({ ...newClient, phone: e.target.value })
                    }
                    className={mobbinInput}
                  />
                  <input
                    placeholder="Correo Electrónico"
                    value={newClient.email}
                    onChange={(e) =>
                      setNewClient({ ...newClient, email: e.target.value })
                    }
                    className={`${mobbinInput} lg:col-span-2`}
                  />
                  <input
                    placeholder="Dirección Completa"
                    value={newClient.address}
                    onChange={(e) =>
                      setNewClient({ ...newClient, address: e.target.value })
                    }
                    className={`${mobbinInput} lg:col-span-2`}
                  />
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveClient}
                    className={mobbinBtnPrimary}
                  >
                    Guardar Cliente
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200 w-full">
                <table className="w-full text-sm text-left min-w-[600px]">
                  <thead className="text-xs text-[#252a50] uppercase bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 font-semibold uppercase">
                        Cliente
                      </th>
                      <th className="px-4 py-3 font-semibold uppercase">RTN</th>
                      <th className="px-4 py-3 font-semibold uppercase">
                        Contacto
                      </th>
                      <th className="px-4 py-3 font-semibold uppercase">
                        Dirección
                      </th>
                      <th className="px-4 py-3 font-semibold uppercase text-center">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredClientsList.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="text-center p-8 text-gray-400"
                        >
                          No se encontraron clientes.
                        </td>
                      </tr>
                    ) : (
                      filteredClientsList.map((cli) => (
                        <tr
                          key={cli.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3 font-semibold text-black break-words whitespace-normal min-w-[150px]">
                            {cli.name}
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-600 text-xs">
                            {cli.rtn || "---"}
                          </td>
                          <td className="px-4 py-3 break-words whitespace-normal min-w-[120px]">
                            <div className="font-medium text-black">
                              {cli.contact || "S/D"}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {cli.email || cli.phone || ""}
                            </div>
                          </td>
                          <td
                            className="px-4 py-3 text-xs text-gray-500 break-words whitespace-normal min-w-[150px] max-w-[200px]"
                            title={cli.address}
                          >
                            {cli.address || "---"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleDeleteClient(cli.id)}
                              className="text-gray-400 hover:text-red-500 p-2 transition-colors"
                            >
                              <Trash2 className="w-4 h-4 mx-auto pointer-events-none" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB: HISTORIAL PRINCIPAL */}
        {activeTab === "history" && (
          <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 animate-in fade-in">
            <div className={`${md3Card} overflow-hidden`}>
              <div className="p-4 sm:p-5 md:p-6 bg-white border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-xl font-bold text-[#252a50]">
                    Documentos
                  </h3>
                </div>
                <div className="flex flex-col sm:flex-row w-full sm:w-auto items-start sm:items-center gap-3">
                  <div className="flex-1 w-full sm:min-w-[280px] relative">
                    <Search className="w-4 h-4 absolute left-4 top-3 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar cliente, número, atención a..."
                      className={`${mobbinInput} pl-10 rounded-full`}
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <select
                      className={`${mobbinInput} rounded-full py-2 px-3 text-xs font-semibold sm:w-auto cursor-pointer`}
                      value={`${historySortConfig.field}-${historySortConfig.order}`}
                      onChange={(e) => {
                        const [field, order] = e.target.value.split("-");
                        setHistorySortConfig({ field, order });
                      }}
                    >
                      <option value="number-desc">Nº Doc (Mayor-Menor)</option>
                      <option value="number-asc">Nº Doc (Menor-Mayor)</option>
                      <option value="date-desc">Fecha (Nuevos)</option>
                      <option value="date-asc">Fecha (Antiguos)</option>
                      <option value="name-asc">Cliente (A-Z)</option>
                      <option value="name-desc">Cliente (Z-A)</option>
                    </select>
                    <div className="hidden md:flex items-center justify-center bg-gray-100 text-[#252a50] px-3 py-2 rounded-full border border-gray-200 font-semibold text-xs whitespace-nowrap">
                      {filteredHistory.length} Registros
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto w-full min-h-[50vh]">
                <table className="w-full text-sm text-left min-w-[800px]">
                  <thead className="text-xs text-[#252a50] uppercase bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Fecha</th>
                      <th className="px-4 py-3 font-semibold">Tipo</th>
                      <th className="px-4 py-3 font-semibold">Cliente</th>
                      <th className="px-4 py-3 font-semibold text-center">
                        Estado
                      </th>
                      <th className="px-4 py-3 font-semibold text-right">
                        Total (L.)
                      </th>
                      <th className="px-4 py-3 font-semibold text-center">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groupedHistory.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="text-center p-12 text-gray-400"
                        >
                          Sin historial registrado.
                        </td>
                      </tr>
                    ) : (
                      groupedHistory.map((group) => (
                        <React.Fragment key={group.docNumber}>
                          <tr className="bg-gray-50/50">
                            <td colSpan="6" className="px-4 py-2">
                              <div className="flex flex-wrap justify-between items-center text-gray-500 gap-2">
                                <span className="font-semibold text-xs uppercase flex items-center gap-2">
                                  <span className="font-mono text-[#252a50]">
                                    {group.docNumber}
                                  </span>
                                </span>
                                <span className="text-[10px] font-medium truncate max-w-[150px] sm:max-w-none">
                                  {group.clientName}
                                </span>
                              </div>
                            </td>
                          </tr>
                          {group.docs.map((docItem) => (
                            <tr
                              key={docItem.id}
                              className="hover:bg-gray-50 transition-colors group/row"
                            >
                              <td className="px-4 py-4 text-gray-600 text-xs whitespace-nowrap">
                                {formatLocalDate(docItem.date)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider border ${
                                    docItem.type.includes("COTIZA")
                                      ? "bg-blue-50 text-blue-700 border-blue-200"
                                      : "bg-gray-100 text-black border-gray-200"
                                  }`}
                                >
                                  {docItem.type.replace("_", " ")}
                                </span>
                              </td>
                              <td className="px-4 py-4 break-words whitespace-normal min-w-[150px]">
                                <div className="font-semibold text-black text-sm">
                                  {docItem.clientName || docItem.description}
                                </div>
                                {docItem.isGovProcess &&
                                  docItem.govProcessCode && (
                                    <div className="text-[9px] text-gray-500 font-mono mt-1 border border-gray-200 px-1 inline-block rounded">
                                      📄 {docItem.govProcessCode}
                                    </div>
                                  )}
                              </td>
                              <td className="px-4 py-4 text-center">
                                <select
                                  value={docItem.status || "PENDIENTE"}
                                  onChange={(e) =>
                                    handleStatusChange(
                                      docItem.id,
                                      e.target.value
                                    )
                                  }
                                  className={`text-[10px] font-bold px-2 py-1 rounded-md outline-none cursor-pointer transition-colors border ${getStatusColor(
                                    docItem.status || "PENDIENTE"
                                  )}`}
                                >
                                  <option value="PENDIENTE">REVISIÓN</option>
                                  <option value="APROBADA">APROBADA</option>
                                  <option value="RECHAZADA">DENEGADA</option>
                                  <option value="PENDIENTE DE PAGO">
                                    POR COBRAR
                                  </option>
                                  <option value="PAGADO">PAGADO</option>
                                </select>
                              </td>
                              <td className="px-4 py-4 text-right font-bold text-black whitespace-nowrap">
                                {formatMoney(docItem.total)}
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex justify-center items-center gap-1 sm:opacity-0 group-hover/row:opacity-100 transition-opacity">
                                  <button
                                    type="button"
                                    onClick={() => setPreviewDoc(docItem)}
                                    className="text-gray-500 hover:text-[#252a50] bg-white border border-gray-200 hover:border-gray-400 w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setConvertModal({
                                        isOpen: true,
                                        docItem: docItem,
                                      })
                                    }
                                    className="text-gray-500 hover:text-[#252a50] bg-white border border-gray-200 hover:border-gray-400 w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                                    title="Convertir"
                                  >
                                    <ArrowRightLeft className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleEditDocument(docItem)}
                                    className="text-gray-500 hover:text-[#252a50] bg-white border border-gray-200 hover:border-gray-400 w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleDuplicateDocument(docItem)
                                    }
                                    className="text-gray-500 hover:text-[#252a50] bg-white border border-gray-200 hover:border-gray-400 w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                                  >
                                    <CopyIcon className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleDeleteHistory(docItem.id)
                                    }
                                    className="text-red-400 hover:text-red-600 bg-white border border-gray-200 hover:border-red-200 w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB: FINANZAS */}
        {activeTab === "finances" && (
          <div className="max-w-6xl mx-auto space-y-4 animate-in fade-in">
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={handleExportCSV}
                className={mobbinBtnOutlined}
              >
                <FileSpreadsheet className="w-4 h-4" /> Bajar CSV
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <div className={`${md3Card} p-5`}>
                <h3 className="font-semibold text-xs text-[#252a50] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Tasas BCH
                </h3>
                {exchangeRates ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
                      <div className="text-[10px] text-gray-500 font-bold mb-1">
                        1 USD
                      </div>
                      <div className="text-base font-bold text-black">
                        L. {(1 / exchangeRates.USD).toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
                      <div className="text-[10px] text-gray-500 font-bold mb-1">
                        1 EUR
                      </div>
                      <div className="text-base font-bold text-black">
                        L. {(1 / exchangeRates.EUR).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Loader2 className="w-5 h-5 animate-spin text-[#252a50] mx-auto" />
                )}
              </div>
              <div className={`${md3Card} p-5`}>
                <h3 className="font-semibold text-xs text-[#252a50] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" /> Conversor
                </h3>
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <input
                    type="number"
                    className={`${mobbinInput} w-full`}
                    value={convAmount}
                    onChange={(e) => setConvAmount(e.target.value)}
                    placeholder="0.00"
                  />
                  <select
                    className={`${mobbinInput} w-full sm:w-auto font-bold py-2 px-3`}
                    value={convFrom}
                    onChange={(e) => setConvFrom(e.target.value)}
                  >
                    <option value="HNL">HNL</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-2 mt-2">
                  <input
                    type="text"
                    readOnly
                    className={`${mobbinInput} w-full font-mono bg-white font-bold`}
                    value={
                      convResult
                        ? convResult.toLocaleString("es-HN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : "0.00"
                    }
                  />
                  <select
                    className={`${mobbinInput} w-full sm:w-auto font-bold py-2 px-3`}
                    value={convTo}
                    onChange={(e) => setConvTo(e.target.value)}
                  >
                    <option value="HNL">HNL</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              <div className={`${md3Card} p-5`}>
                <button
                  type="button"
                  onClick={() => setShowCalculator(!showCalculator)}
                  className="w-full flex justify-between items-center font-semibold text-sm text-[#252a50]"
                >
                  <span className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-gray-500" /> Calculadora
                  </span>
                  <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center">
                    {showCalculator ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </span>
                </button>
                {showCalculator && (
                  <div className="mt-4">
                    <div className="bg-gray-100 p-3 rounded-xl mb-3 text-right text-lg font-mono font-medium h-12 flex items-center justify-end text-black">
                      {calcInput || "0"}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        "7",
                        "8",
                        "9",
                        "/",
                        "4",
                        "5",
                        "6",
                        "*",
                        "1",
                        "2",
                        "3",
                        "-",
                        "C",
                        "0",
                        ".",
                        "+",
                      ].map((btn) => (
                        <button
                          type="button"
                          key={btn}
                          onClick={() => handleCalcClick(btn)}
                          className={`p-2 rounded-xl font-semibold text-sm transition-colors border ${
                            btn === "C"
                              ? "text-red-500 border-red-200 bg-white hover:bg-red-50"
                              : ["/", "*", "-", "+"].includes(btn)
                              ? "bg-gray-100 border-transparent"
                              : "bg-white border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          {btn}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleCalcClick("=")}
                        className="col-span-4 bg-[#252a50] text-white p-2 rounded-xl font-bold text-lg mt-1"
                      >
                        =
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div
                className={`${md3Card} p-6 flex flex-col justify-center items-center text-center`}
              >
                <div className="text-xs text-[#252a50] font-semibold uppercase tracking-widest mb-2">
                  Ingresos Emitidos
                </div>
                <div className="text-4xl font-bold text-[#252a50] tracking-tight">
                  L. {formatMoney(salesTotal)}
                </div>
                <p className="text-[10px] text-gray-400 mt-2 font-medium uppercase">
                  Facturas y Recibos Totales
                </p>
              </div>
              <div
                className={`${md3Card} p-6 flex flex-col justify-center items-center text-center`}
              >
                <div className="text-xs text-[#252a50] font-semibold uppercase tracking-widest mb-2">
                  Proyección Segura
                </div>
                <div className="text-4xl font-bold text-[#252a50] tracking-tight">
                  L. {formatMoney(approvedQuotesTotal)}
                </div>
                <p className="text-[10px] text-gray-400 mt-2 font-medium uppercase">
                  Cotizaciones Aprobadas
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
